// Help content for the SupportHelp / InfoComponent drawer.
// Each entry is keyed by the app route and is rendered generically in
// InfoComponent.js as an intro paragraph followed by a Bootstrap Accordion.
//
// Structure:
//   {
//     route: "/SomeRoute",
//     intro: "Optional intro paragraph shown above the accordion",
//     sections: [
//       {
//         title: "Accordion header",
//         body: [
//           "A plain string is rendered as a paragraph",
//           { list: ["bullet 1", "bullet 2"] } // rendered as a <ul>
//         ]
//       }
//     ]
//   }
//
// Text below is the full Help Section content. All strings pass through
// i18next t(); missing keys fall back to the English text, so it displays as-is.

export const helpContent = [
  // ===================== USERS =====================
  {
    route: "/administrator",
    sections: [
      {
        title: "Administrator Listings",
        body: [
          "The Administrator module allows you to view and access administrator records maintained within the Vehicle Tracking System. This module serves as a centralized location for managing administrator information and provides quick access to administrator details required for operational, management, and administrative purposes.",
          "The Administrator Listing page displays all administrator records available in the system in an organized format. Each record includes important information such as the administrator's profile image, name, email address, and mobile number. This allows you to quickly identify administrators and review essential information without opening individual profiles.",
          "The listing page helps you maintain visibility of all administrator records and acts as a single point of reference for administrator-related information throughout the system.",
        ],
      },
      {
        title: "Administrator Search and Record Identification",
        body: [
          "The administrator listing enables you to easily locate administrator records by reviewing the available information displayed on the screen. The organized layout of administrator data helps you quickly identify the required administrator and access detailed information whenever needed.",
          "The administrator records displayed in the listing provide a quick overview of available administrators and help you efficiently manage and review administrator information.",
        ],
      },
      {
        title: "View Administrator Details",
        body: [
          "The View feature allows you to access detailed information about a selected administrator. By clicking the View icon available in the Action column, you can open a dedicated profile page containing complete administrator information.",
          "The View Details page provides a comprehensive overview of the selected administrator and allows you to review both personal and employment-related information associated with the administrator account.",
          "This feature is useful for verifying administrator information, reviewing profile details, validating contact information, and maintaining accurate records within the system.",
        ],
      },
      {
        title: "Administrator Profile",
        body: [
          "The Administrator Profile page presents complete information related to the selected administrator in a structured and user-friendly format. The profile page is designed to provide a detailed overview of administrator information while allowing you to review records efficiently.",
          "The profile includes both Contact Information and General Information sections, ensuring that all relevant administrator details are available from a single screen.",
          "Profile Image:",
          "The administrator profile may display a profile image associated with the administrator account. The profile image helps you visually identify administrators and distinguish between multiple administrator records within the system.",
          "This feature improves administrator recognition and supports accurate record management.",
        ],
      },
      {
        title: "Contact Information",
        body: [
          "The Contact Information section contains the primary communication details of the administrator. This section provides essential information required for identification and communication purposes.",
          "The Contact Information section may include:",
          { list: ["Administrator Name", "Mobile Number", "Email Address"] },
          "This information helps you verify administrator contact details and access communication information whenever required. Maintaining accurate contact information ensures effective communication and improves operational efficiency.",
          "Administrator Name:",
          "The administrator name identifies the administrator associated with the profile. This information helps you distinguish between administrator records and verify account ownership.",
          "Mobile Number:",
          "The registered mobile number provides a direct communication channel for contacting the administrator. This information can be used for communication, verification, and administrative coordination purposes.",
          "Email Address:",
          "The registered email address serves as the official communication address associated with the administrator account. You can review this information to verify administrator records and maintain accurate communication details.",
        ],
      },
      {
        title: "General Information",
        body: [
          "The General Information section provides additional details related to the administrator profile. This section contains personal and employment-related information that helps you gain a complete understanding of the administrator record.",
          "The General Information section may include:",
          {
            list: [
              "Address",
              "City",
              "Nationality",
              "Date of Birth",
              "Joining Date",
              "Gender",
              "Employee Code",
              "Status",
            ],
          },
          "These details provide a comprehensive overview of the administrator profile and help maintain accurate personnel records within the organization.",
          "Address Information: The address field contains the administrator's registered address information. This information helps maintain accurate records and supports administrative documentation requirements.",
          "City Information: The city field indicates the administrator's registered city and assists in maintaining location-related information for organizational records.",
          "Nationality Information: The nationality field identifies the administrator's nationality and helps maintain complete personal information within the administrator profile.",
          "Date of Birth: The date of birth provides personal identification information associated with the administrator profile and contributes to accurate record maintenance.",
          "Joining Date: The joining date indicates when the administrator became associated with the organization. This information assists in maintaining employment records and administrative history.",
          "Gender Information: The gender field records the administrator's gender information and forms part of the complete administrator profile.",
          "Employee Code: The employee code serves as a unique identifier for the administrator within the organization. This information helps distinguish administrator records and supports internal administrative processes.",
          "Administrator Status: The status field indicates the current state of the administrator account within the system. Status information helps you determine whether the administrator account is active and available for system operations.",
        ],
      },
      {
        title: "Reviewing Administrator Information",
        body: [
          "The Administrator module is primarily designed to help you review, verify, and maintain administrator records. You can access administrator profiles, verify contact details, review employment information, and ensure that administrator records remain accurate and up to date.",
          "The centralized administrator listing and detailed profile view provide a convenient way to access administrator information while supporting efficient administration and record management.",
          "By utilizing these administrator-related features, you can efficiently review administrator records, verify administrator information, access contact details, and maintain accurate administrative data within the Vehicle Tracking System. If you have any questions or need further assistance, please don't hesitate to reach out to our support team. They will be happy to assist you.",
        ],
      },
    ],
  },
  {
    route: "/TransportManager",
    sections: [
      {
        title: "Adding a Transport Manager",
        body: [
          "To add a new transport manager, click on the Add Transport Manager button located on the top right side of the screen. This will open a form where you can enter transport manager details.",
          "When adding a transport manager, you need to provide the following details:",
          "Transport Manager Information: Enter the first name, last name, email address, mobile number, and password details. The mobile number and password are mandatory fields, and they are required for login and authentication purposes.",
          "General Information: Enter additional details such as address, city, nationality, gender, date of birth, joining date, and employee code. These details help in maintaining complete transport manager records in the system.",
          "After entering all required details, click on the Submit button to save the transport manager. Once saved, the transport manager will be added to the Active list and will be able to access the system based on assigned permissions.",
          "Click on Cancel if you do not want to save the details and want to close the form without creating a transport manager.",
        ],
      },
      {
        title: "Transport Manager Listings",
        body: [
          "You can view transport managers in two sections: Active and History/Inactive. Each section helps you manage transport managers based on their current status.",
        ],
      },
      {
        title: "Active Section",
        body: [
          "In the Active section, you can view all currently active transport managers. Each transport manager is displayed in a card format.",
          "Each card shows transport manager details such as name, email address, and mobile number. This helps you quickly identify and contact a transport manager.",
          "You can also use the search bar to find a transport manager by entering name, email, or mobile number.",
          "Each card has a more options menu (three-dot menu). From this menu, you can perform the following actions:",
          "View: Opens the full transport manager details page where you can see contact information and general information such as address, city, nationality, date of birth, joining date, gender, employee code, and status.",
          "Edit: Allows you to update transport manager details such as name, email, mobile number, and other general information. After making changes, click Update to save the changes.",
          "Change Password: Allows you to update the login password of the transport manager by entering a new password and confirming it.",
          "Block: Used to temporarily disable a transport manager. Once blocked, the transport manager will be moved from Active to History section and will not be able to access the system.",
          "Resign: Used when a transport manager leaves the organization. Once resigned, the transport manager will be moved to the History section and will no longer be active in the system.",
        ],
      },
      {
        title: "History / Inactive Section",
        body: [
          "In the History or Inactive section, you can view transport managers who are blocked, resigned, or inactive.",
          "Each transport manager is shown in a card format with name, email, and mobile number.",
          "You can search transport managers using name, email, or mobile number in the search bar.",
          "Each card has a more options menu where you can perform the following actions:",
          "View: Opens full transport manager details including contact and general information.",
          "Edit: Allows you to update transport manager information if required.",
          "Change Password: Allows you to reset or update the password of the transport manager.",
          "Unblock: Used to activate a blocked transport manager. Once unblocked, the transport manager will be moved back to the Active section and will regain access to the system.",
        ],
      },
      {
        title: "Search Functionality",
        body: [
          "You can search transport managers in both Active and History sections using the search bar.",
          "You can search by:",
          { list: ["Transport Manager Name", "Email Address", "Mobile Number"] },
          "Search results will be displayed automatically as you type.",
        ],
      },
      {
        title: "Share Functionality",
        body: [
          "A Share button is available on the screen to share transport manager details.",
          "By clicking Share, you can share basic information such as name, email address, and mobile number with other users or systems for communication purposes.",
        ],
      },
    ],
  },
  {
    route: "/Drivers",
    sections: [
      {
        title: "Adding a Driver",
        body: [
          "To add a new driver, click on the Add Driver / New Driver button located on the right side of the screen. This will open a form where you can enter driver details.",
          "When adding a driver, you will need to provide the following details:",
          "Driver Information: Enter details such as first name and last name. First name is a mandatory field, and last name is optional. You can also upload a profile picture of the driver.",
          "Contact Details: Provide the mobile number and email address of the driver. Mobile number is a required field, while email is optional.",
          "Security Information: Set a password and confirm password for driver login access. Both fields are required and must match before saving.",
          "Address Information: Enter address, city, and nationality of the driver.",
          "By utilizing these driver-related features, you can efficiently manage and track driver information within the system. If you have any questions or need further assistance, please contact the support team.",
        ],
      },
      {
        title: "Driver Listings",
        body: [
          "You can view driver listings categorized into sections: Active and Inactive. Each section offers different options to help manage drivers based on their status.",
        ],
      },
      {
        title: "Active Section",
        body: [
          "In the Active section, you can view all active drivers. Each driver is displayed in a card format showing driver name, email address, and mobile number.",
          "You can use the search bar to search drivers by entering driver name, email address, or mobile number.",
          "Each driver card has a more options menu where you can perform the following actions:",
          "View: Opens the driver details including contact information and general information.",
          "Edit: Allows you to update driver details.",
          "Change Password: Allows you to set a new password for the driver by entering and confirming password.",
          "Block: Used to block a driver. Once blocked, the driver will be moved to the Inactive section.",
        ],
      },
      {
        title: "Inactive Section",
        body: [
          "In the Inactive section, you can view all inactive drivers. These include blocked drivers.",
          "You can use the search bar to search drivers by name, email address, or mobile number.",
          "Each driver card has a more options menu where you can perform the following actions:",
          "View: Opens driver details.",
          "Edit: Allows you to update driver details.",
          "Change Password: Allows you to update password.",
          "Unblock: Used to activate a blocked driver. Once unblocked, the driver will be moved back to the Active section.",
        ],
      },
      {
        title: "Driver Details View",
        body: [
          "When you click on View, it will open driver details.",
          "It includes Contact Information such as name, mobile number, and email address.",
          "It also includes General Information such as address, city, nationality, and other driver details.",
          "From this page, you can also access options like edit, change password, block, or unblock using the menu.",
        ],
      },
    ],
  },
  {
    route: "/DeliveryPerson",
    sections: [
      {
        title: "Adding a Delivery Person",
        body: [
          "To add a new delivery person, click on the + Delivery Person button located on the main screen. This will open a form where you can enter delivery person details.",
          "When adding a delivery person, you will need to provide the following details:",
          "Profile Information: Enter first name and last name of the delivery person. First name is mandatory and last name is optional. You can also upload a profile picture for identification.",
          "Contact Information: Provide mobile number and email address of the delivery person. Mobile number is mandatory and email is optional. The country code is pre-filled (+91 for India).",
          "Security Information: Set a user password and confirm password for secure login access. Both fields are mandatory and must match before saving the details.",
          "Address Information: Enter address, city, and nationality of the delivery person.",
          "After entering all required details, click on the Submit button to save the delivery person profile. Once saved, the delivery person will be added to the system with a unique profile.",
        ],
      },
      {
        title: "Delivery Person Listings",
        body: [
          "You can view delivery person listings categorized into sections: Active and Inactive/History. Each section helps manage delivery personnel based on their current status.",
        ],
      },
      {
        title: "Active Section",
        body: [
          "In the Active section, you can view all active delivery persons. Each delivery person is displayed with details such as name, email address, mobile number, and assigned vehicle information.",
          "You can use the search bar to find delivery persons by entering name, email address, or mobile number.",
          "Each delivery person record has an action menu (three dots) where you can perform the following actions:",
          "View: Opens delivery person details including personal and contact information.",
          "Edit: Allows you to update delivery person details such as name, contact information, and address.",
          "Reassign Vehicle: Allows you to update or change the assigned vehicle for the delivery person.",
          "Deactivate: Used to deactivate a delivery person. Once deactivated, the delivery person is moved to the Inactive section.",
        ],
      },
      {
        title: "Inactive Section",
        body: [
          "In the Inactive section, you can view all inactive delivery persons. These include deactivated personnel.",
          "You can use the search bar to find delivery persons by name, email, or mobile number.",
          "Each delivery person record has an action menu where you can perform the following actions:",
          "View: Opens delivery person details.",
          "Edit: Allows you to modify delivery person information.",
          "Reactivate: Used to activate a previously deactivated delivery person and move them back to the Active section.",
        ],
      },
      {
        title: "Delivery Person Details View",
        body: [
          "When you click on View, it will open full delivery person details.",
          "It includes personal information such as name, mobile number, email address, address, city, and nationality.",
          "It also includes assigned vehicle details and status information.",
          "From this page, you can also access actions like edit, reassign vehicle, deactivate, or reactivate using the menu.",
        ],
      },
      {
        title: "Searching",
        body: [
          "You can search delivery persons in both Active and Inactive sections using the search bar.",
          "You can search by:",
          { list: ["Name", "Email Address", "Mobile Number"] },
          "Search results are filtered automatically based on input.",
        ],
      },
    ],
  },
  {
    route: "/FleetManager",
    sections: [
      {
        title: "Adding a Fleet Manager",
        body: [
          "To add a new fleet manager, click on the + Fleet Manager button located on the main screen. This will open a form where you can enter fleet manager details.",
          "When adding a fleet manager, you will need to provide the following details:",
          "Profile Information: Enter first name, last name, email address, and mobile number of the fleet manager. First name and mobile number are mandatory fields, while last name and email are optional.",
          "Security Information: Set a user password and confirm password for secure login access. Both fields are mandatory and must match before saving the details.",
          "Address Information: Enter address and city details of the fleet manager.",
          "Nationality Information: Select nationality for the fleet manager. India is selected by default.",
          "After entering all required details, click on the Submit button to save the fleet manager. Once saved, the fleet manager will be added under Active status.",
        ],
      },
      {
        title: "Fleet Manager Listings",
        body: [
          "You can view fleet manager listings categorized into Active and Inactive sections. Each section helps manage fleet managers based on their current status.",
        ],
      },
      {
        title: "Search Functionality",
        body: [
          "You can search fleet managers in both Active and Inactive sections using the search bar.",
          "You can search by:",
          { list: ["Name", "Email Address", "Mobile Number"] },
          "Search results are filtered automatically based on input.",
        ],
      },
      {
        title: "Export Data",
        body: [
          "Fleet manager details can be exported for external use or reporting purposes.",
        ],
      },
      {
        title: "Active Section",
        body: [
          "In the Active section, you can view all active fleet managers. Each fleet manager is displayed in a card format showing name, email address, and mobile number. Profile picture is shown if available.",
          "You can use the search bar to find fleet managers by entering name, email address, or mobile number.",
          "Each fleet manager card has an action menu where you can perform the following actions:",
          "View: Opens fleet manager details including personal, contact, and general information.",
          "Edit: Allows you to update fleet manager details such as name, email, and mobile number.",
          "Deactivate: Used to deactivate a fleet manager and move them to the Inactive section.",
        ],
      },
      {
        title: "Inactive Section",
        body: [
          "In the Inactive section, you can view all inactive fleet managers.",
          "You can search fleet managers using name, email, or mobile number.",
          "Each fleet manager card has an action menu where you can perform the following actions:",
          "View: Opens fleet manager details.",
          "Edit: Allows you to update fleet manager information.",
          "Reinstate: Used to reactivate a fleet manager and move them back to the Active section.",
        ],
      },
      {
        title: "Fleet Manager Details View",
        body: [
          "When you click on View, it opens full fleet manager details.",
          "It includes personal information such as name, email address, mobile number, address, and city.",
          "It also displays status information and profile details.",
          "From this screen, you can also perform actions like edit, deactivate, or reinstate using the menu.",
        ],
      },
    ],
  },
  {
    route: "/VehicleAssistants",
    sections: [
      {
        title: "Viewing Vehicle Assistants",
        body: [
          "When you open the Vehicle Assistants module, you will see assistants categorized into Active and Inactive sections.",
          "Each assistant is displayed in a card format showing details such as name, email address, mobile number, and vehicle assignment.",
          "You can use the search bar to find assistants by entering name, email address, or mobile number.",
        ],
      },
      {
        title: "Adding a Vehicle Assistant",
        body: [
          "To add a new vehicle assistant, click on the + Vehicle Assistant button located on the main screen. This will open a form where you can enter assistant details.",
          "When adding a vehicle assistant, you will need to provide the following details:",
          "Profile Information: Enter first name, last name, and email address of the assistant. First name is mandatory, while last name and email are optional.",
          "Contact Information: Provide the mobile number of the assistant. Mobile number is mandatory for creating the assistant profile.",
          "Security Information: Set a user password and confirm password for secure login access. Both fields are mandatory and must match before saving the details.",
          "Address Information: Enter address and city details of the assistant.",
          "Nationality Information: Nationality is set to India by default and can be modified if required.",
          "After entering all required details, click on the Submit button to save the assistant. Once saved, the assistant will be added to the Active section.",
        ],
      },
      {
        title: "Active Section",
        body: [
          "In the Active section, you can view all active vehicle assistants.",
          "Each assistant card displays name, email address, mobile number, and assigned vehicle details.",
          "Each record can be searched using name, email, or mobile number through the search bar.",
        ],
      },
      {
        title: "Inactive Section",
        body: [
          "In the Inactive section, you can view all inactive vehicle assistants.",
          "You can search assistants using name, email, or mobile number.",
          "This section helps manage assistants who are not currently active in the system.",
        ],
      },
      {
        title: "Export Data",
        body: [
          "You can export vehicle assistant data by clicking the export option available on the top right side of the screen.",
          "You can download data in:",
          { list: ["PDF format", "Excel format"] },
        ],
      },
    ],
  },

  // ===================== HARDWARE FEATURE SET =====================
  {
    route: "/FeatureSet",
    sections: [
      {
        title: "Hardware Feature Set",
        body: [
          "The Hardware Feature Set module allows you to manage and monitor hardware-based functionalities associated with vehicles in the Vehicle Tracking System. Hardware features are physical devices and sensors installed in vehicles that collect, process, and transmit vehicle-related data. These features work together with the software platform to provide real-time monitoring, safety management, operational control, and vehicle diagnostics.",
          "The Hardware Feature Set module provides a centralized interface where you can view vehicles associated with specific hardware features, monitor feature availability, and enable or disable hardware functionalities as required.",
        ],
      },
      {
        title: "Hardware Feature Set Listings",
        body: [
          "The Hardware Feature Set listing displays vehicles associated with selected hardware features. Each vehicle card provides important information including Vehicle Name, Registration Number, IMEI Number, Hardware Name, and Vehicle Category.",
          "The listing allows you to quickly identify vehicles, review assigned hardware configurations, and monitor hardware feature availability across the fleet. You can also enable or disable specific hardware features using the toggle option available for each vehicle.",
          "The module includes export options that allow you to export hardware-related information in various formats for reporting and analysis purposes.",
        ],
      },
      {
        title: "Tracking",
        body: [
          "The Tracking feature allows you to monitor the real-time location and movement of vehicles. It provides visibility into vehicle positions, routes traveled, and operational activity. This feature helps fleet managers monitor vehicle utilization and improve operational efficiency.",
          "The Tracking section displays vehicle-related information including Vehicle Name, Registration Number, IMEI Number, Hardware Name, and Vehicle Category. This information enables you to identify tracked vehicles and review tracking device details associated with each vehicle.",
          "Tracking supports location monitoring, route management, and vehicle visibility throughout daily operations.",
        ],
      },
      {
        title: "Over Speeding",
        body: [
          "The Over Speeding feature is designed to monitor vehicle speed and identify instances where vehicles exceed predefined speed limits. This functionality helps organizations promote safer driving practices and improve fleet safety management.",
          "The Over Speeding section displays vehicles that are configured for speed monitoring and provides access to important vehicle information including Vehicle Name, Registration Number, IMEI Number, Hardware Name, and Vehicle Category.",
          "This feature assists fleet managers in monitoring driving behavior and improving compliance with operational safety requirements.",
        ],
      },
      {
        title: "Temperature Sensors",
        body: [
          "The Temperature Sensor feature enables temperature monitoring within vehicles equipped with temperature sensing devices. This functionality is commonly used for refrigerated vehicles, cold-chain transportation, and temperature-sensitive cargo operations.",
          "The Temperature Sensor section provides visibility into vehicles equipped with temperature monitoring hardware and displays relevant vehicle details for identification and management purposes.",
          "This feature helps ensure that transported goods remain within required temperature ranges and supports regulatory compliance for temperature-controlled transportation.",
        ],
      },
      {
        title: "IVMS (Intelligent Vehicle Management System)",
        body: [
          "The IVMS feature provides advanced vehicle monitoring and management capabilities. It integrates vehicle tracking data with operational information to provide a comprehensive view of vehicle performance and activity.",
          "The IVMS section allows you to monitor vehicles equipped with IVMS hardware and access associated vehicle information. This feature supports operational monitoring, driving behavior analysis, route management, and fleet performance optimization.",
          "IVMS improves visibility into fleet operations and helps organizations make informed operational decisions.",
        ],
      },
      {
        title: "Fuel Sensor",
        body: [
          "The Fuel Sensor feature enables monitoring of vehicle fuel levels and fuel consumption patterns. Vehicles equipped with fuel sensors can provide accurate fuel-related information that assists in fuel management and operational cost control.",
          "The Fuel Sensor section displays vehicles configured with fuel monitoring hardware and provides access to relevant vehicle identification information.",
          "This feature helps organizations track fuel usage, identify unusual fuel consumption patterns, and improve fuel efficiency across the fleet.",
        ],
      },
      {
        title: "Seat Belt Sensor",
        body: [
          "The Seat Belt Sensor feature monitors seat belt usage within vehicles equipped with seat belt detection systems. This functionality helps improve driver safety and encourages compliance with safety regulations.",
          "The Seat Belt Sensor section displays vehicles associated with seat belt monitoring hardware and provides related vehicle information for review and management.",
          "This feature supports driver safety initiatives and contributes to safer fleet operations.",
        ],
      },
      {
        title: "Card Reader",
        body: [
          "The Card Reader feature is used to identify and authenticate authorized drivers through card-based verification systems. Vehicles equipped with card readers can validate driver access and ensure that only authorized personnel operate the vehicle.",
          "The Card Reader section displays vehicles configured with card reader hardware and provides access to associated vehicle information.",
          "This feature improves security, driver accountability, and operational control within the fleet.",
        ],
      },
      {
        title: "Immobilization",
        body: [
          "The Immobilization feature provides the ability to remotely disable vehicle operation when necessary. This functionality is commonly used for vehicle security and theft prevention purposes.",
          "The Immobilization section displays vehicles equipped with immobilization hardware and allows you to review associated vehicle information.",
          "This feature enhances vehicle security and provides organizations with additional control over fleet assets when required.",
        ],
      },
      {
        title: "Offline Map",
        body: [
          "The Offline Map feature enables location tracking and navigation capabilities even in areas with limited or no internet connectivity. Offline maps allow vehicle location information and route data to remain accessible without relying on continuous network availability.",
          "The Offline Map section displays vehicles configured for offline mapping functionality and provides associated vehicle information.",
          "This feature is particularly useful for vehicles operating in remote locations or areas with unstable network coverage.",
        ],
      },
      {
        title: "LV CAN",
        body: [
          "The LV CAN (Low Voltage Controller Area Network) feature supports communication with vehicle electronic systems and onboard components. It enables the collection of vehicle operational data from supported vehicle networks.",
          "The LV CAN section displays vehicles associated with LV CAN hardware and provides access to related vehicle information.",
          "This feature assists in vehicle diagnostics, operational monitoring, and enhanced vehicle data collection.",
        ],
      },
      {
        title: "Vehicle Information",
        body: [
          "Throughout the Hardware Feature Set module, you can access important vehicle-related information including:",
          {
            list: [
              "Vehicle Name / Vehicle Number",
              "Registration Number",
              "IMEI Number",
              "Hardware Name",
              "Vehicle Category",
            ],
          },
          "This information helps you identify vehicles, verify hardware assignments, and manage hardware configurations efficiently.",
        ],
      },
      {
        title: "Export and Reporting",
        body: [
          "The Hardware Feature Set module provides export functionality that allows you to generate reports and export hardware-related information. Available export options may include PDF and Excel formats, enabling you to analyze data, maintain records, and share information when required.",
        ],
      },
    ],
  },

  // ===================== TRIP MANAGEMENT =====================
  {
    route: "/TripManagement",
    sections: [
      {
        title: "Trip Management",
        body: [
          "The Trip Management module helps fleet managers and administrators create, schedule, monitor, and manage transportation trips within the Vehicle Tracking System. This module provides complete visibility of trip activities, vehicle assignments, driver allocations, route planning, and trip completion status.",
          "Using this module, you can efficiently organize transportation operations, monitor trip progress, manage trip stops, and maintain historical records of completed trips.",
        ],
      },
      {
        title: "Trip Listings",
        body: [
          "The Trip section displays all trips created within the system. Trips are categorized into different sections based on their current status, allowing you to easily manage and track transportation activities.",
          "You can search and filter trips using details such as Trip Name, Driver Name, Vehicle Name, Email Address, Contact Number, and Date.",
        ],
      },
      {
        title: "Planned Section",
        body: [
          "The Planned section displays all trips that have been scheduled in advance. These trips are created before execution and include assigned vehicles, drivers, routes, and schedules.",
          "This section helps transport managers organize upcoming transportation activities, allocate resources efficiently, and ensure operational readiness before trip execution.",
          "You can search planned trips using available filters and review trip details whenever required.",
        ],
      },
      {
        title: "Unplanned Section",
        body: [
          "The Unplanned section contains trips that were created without prior scheduling. These trips are generally used for emergency transportation requests, urgent deliveries, or unexpected operational requirements.",
          "This section enables managers to quickly assign available vehicles and drivers while maintaining visibility over unscheduled transportation activities.",
        ],
      },
      {
        title: "Completed Section",
        body: [
          "The Completed section displays all trips that have been successfully completed.",
          "You can review completed trips for operational analysis, performance monitoring, reporting, auditing, and future planning purposes.",
          "The completed trip history provides valuable information regarding vehicle utilization, driver performance, and transportation efficiency.",
        ],
      },
      {
        title: "New Trip",
        body: [
          "The + New Trip button allows you to create and schedule a new trip within the system.",
          "When creating a trip, you can define important trip information including:",
          {
            list: [
              "Trip Name – Identifies the trip within the system.",
              "Trip Category – Specifies the type of transportation activity being performed.",
              "Vehicle Assignment – Assigns a vehicle for trip execution.",
              "Driver Assignment – Assigns an authorized driver to the trip.",
              "Start Location – Defines the trip origin.",
              "Destination Location – Defines the trip destination.",
              "Trip Schedule – Configures trip dates and timing.",
              "Recurring Schedule – Allows trips to repeat on selected days.",
            ],
          },
          "The trip creation process helps ensure that vehicles and drivers are properly allocated before transportation activities begin.",
        ],
      },
      {
        title: "Dispatch Trip Management",
        body: [
          "The Dispatch Trip functionality enables transport managers to organize and control transportation requests efficiently.",
          "Dispatch trips include vehicle assignment, driver allocation, scheduling information, route planning, and trip monitoring.",
          "The system supports recurring trips, allowing organizations to schedule transportation activities on specific days such as Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, and Sunday.",
          "This functionality reduces manual effort and simplifies repetitive trip scheduling.",
        ],
      },
      {
        title: "Trip Monitoring",
        body: [
          "The Trip Management module provides visibility into ongoing transportation activities.",
          "Managers can monitor:",
          {
            list: [
              "Trip Status",
              "Assigned Vehicles",
              "Assigned Drivers",
              "Trip Routes",
              "Scheduled Trips",
              "Completed Trips",
            ],
          },
          "This centralized monitoring helps improve fleet utilization and operational efficiency.",
        ],
      },
      {
        title: "Stop Management",
        body: [
          "The Stop section allows you to manage pickup and drop-off locations associated with transportation trips.",
          "Each stop record contains important information that helps you plan and monitor transportation activities effectively.",
          "Stop Listings: The Stop listing page displays all configured trip stops within the system. For every stop, you can view:",
          {
            list: [
              "Stop Name",
              "Pick-Up Point Trip",
              "Pick-Up Point Code",
              "Pick-Up Point Priority",
              "Distance From Customer (KM)",
              "Stop Latitude",
              "Stop Longitude",
            ],
          },
          "These details provide complete visibility into trip stop locations and route planning requirements.",
          "Stop Priority: The Pick-Up Point Priority field determines the importance and sequence of a stop during trip execution. Priority-based planning helps ensure that critical stops are serviced first, improving route efficiency and customer satisfaction.",
          "Distance Information: Distance From Customer displays the distance between the stop location and the customer location. This information helps managers optimize routes, reduce travel time, and improve operational efficiency.",
          "Location Coordinates: Latitude and Longitude values provide the exact geographical location of each stop. These coordinates help with navigation, route planning, and location tracking.",
          "Stop Actions: Each stop includes management actions that allow you to view stop information, edit stop details, and delete unnecessary stop records. These actions help maintain accurate and up-to-date stop information within the system.",
          "Exporting Stop Data: You can export stop information using the Export option available on the page. Supported export formats may include PDF and Excel. Exported reports can be used for route analysis, planning, auditing, and operational reporting.",
        ],
      },
      {
        title: "Trip Routes",
        body: [
          "The Trip Routes section helps you manage and monitor transportation routes assigned to trips. This section provides visibility into route assignments, trip progress, and transportation execution.",
          "Route Information: You can view route-related details including Trip Identification, Start Location, Destination Location, Date and Time, Assigned Vehicle, Assigned Driver, and Current Status. These details help transport managers monitor route execution and transportation performance.",
          "Route Assignment: Routes can be assigned to vehicles and drivers based on operational requirements. Proper route assignment improves fleet coordination, reduces scheduling conflicts, and ensures efficient transportation planning.",
          "Route Status Monitoring: The system continuously tracks route progress and displays current route status information. Depending on operational activities, route statuses may include Assigned, Pending, In Progress, and Completed. This allows managers to monitor transportation activities and make informed operational decisions.",
          "Route Search and Filtering: You can quickly locate route information using available search and filter options. Common route filters include Trip ID, Start Location, Destination Location, Date, Time, and Status. These filters improve navigation and help you quickly locate required route information.",
          "By utilizing the Trip Management module, organizations can efficiently manage transportation operations, optimize resource utilization, monitor trip execution, and maintain complete visibility over trips, stops, routes, drivers, and vehicles within the Vehicle Tracking System.",
        ],
      },
    ],
  },
  {
    route: "/VehicleAvailability",
    sections: [
      {
        title: "Viewing Vehicle Availability",
        body: [
          "Once inside the module, you can view vehicle availability in two formats:",
          "Month View: Displays all scheduled vehicle activities for the entire month in a calendar layout.",
          "Agenda View: Displays vehicle activities in a date-wise list format for quick reference.",
          "Each date on the calendar is marked with a specific color indicating vehicle status:",
          {
            list: [
              "Red: Scheduled Trip",
              "Blue: Instant Trip",
              "Grey: Dispatched Vehicle",
              "Cyan: Completed Trip",
              "Light Beige: Weekends",
            ],
          },
          "You can click on any date to view detailed trip information including assigned vehicles and schedule details.",
          "Filtering and Searching Vehicles: Use the vehicle category dropdown to filter vehicles based on type. Click Show Available Vehicles to display only vehicles that are free for scheduling. Use the Clear Filter option to reset all applied filters and return to full calendar view. These filters help you quickly identify available resources without manually checking each entry.",
          "Navigating the Calendar: Today Button quickly jumps to the current date, Back Button moves to the previous month, Next Button moves to the next month, and Date Selection lets you click on any specific date to view detailed trip and vehicle assignment information. This allows you to plan both current and future schedules efficiently.",
          "Managing Trip Status and Vehicle Allocation: The calendar helps track and manage vehicle lifecycle across trips. View which vehicles are currently scheduled, dispatched, or completed; ensure a vehicle is not assigned to multiple trips at the same time; monitor instant bookings and adjust availability accordingly; and identify completed trips for reporting and analysis. This ensures proper fleet distribution and avoids booking conflicts.",
        ],
      },
    ],
  },
  {
    route: "/AvailableVehicleList",
    sections: [
      {
        title: "Viewing Driver Availability",
        body: [
          "Inside the module, driver availability is displayed in a calendar format:",
          "Month View: Shows all driver assignments across the month.",
          "Agenda View: Displays driver schedules in a date-wise list format.",
          "Each date is marked with color-coded statuses for easy identification:",
          {
            list: [
              "Red: Scheduled Trip (Driver assigned in advance)",
              "Blue: Instant Trip (Immediate dispatch)",
              "Grey: Dispatched (Driver currently on trip)",
              "Teal: Completed Trip (Trip finished)",
              "Beige: Weekends (Non-working or reference days)",
            ],
          },
          "You can click on any date to view detailed driver assignment information, including trip details and status.",
          "Searching and Filtering Drivers: Use the search bar to find a driver by name. Apply filters to view specific statuses such as Scheduled, Completed, or Dispatched trips. Reset filters anytime using the Clear Filter option. These tools help dispatchers quickly identify the right driver for assignment without manual checking.",
          "Assigning Available Drivers: Click on the Show Available Driver button. The system will display all drivers who are currently free and eligible for assignment. Select a driver from the list to assign them to a new trip immediately. This feature ensures fast allocation during urgent or instant booking requests.",
          "Tracking Driver Status: The system continuously tracks driver activity in real time. View which drivers are currently assigned to trips, identify idle or available drivers, monitor ongoing and completed trips, and avoid assigning multiple trips to the same driver. This helps maintain smooth operations and prevents scheduling conflicts.",
        ],
      },
    ],
  },

  // ===================== VEHICLE EXPENSES =====================
  {
    route: "/VehicleAccident",
    sections: [
      {
        title: "Vehicle Accident Listing",
        body: [
          "The Vehicle Accident Module is designed to record, manage, and maintain accident-related information for fleet vehicles. It helps fleet managers and administrators keep a complete record of vehicle incidents, including accident location, expenses incurred, driver details, accident descriptions, supporting documents, and incident dates.",
          "This module ensures that all accident records are properly documented for operational tracking, maintenance planning, insurance claims, compliance requirements, and future reference. You can create new accident records, search existing records, view detailed accident information, update records when required, and remove obsolete entries.",
          "By maintaining accurate accident history, organizations can monitor vehicle safety, analyze accident trends, control repair costs, and improve fleet management processes.",
          "The Vehicle Accident listing page displays all recorded accident entries in a structured format. You can quickly review accident information, search records, and perform actions such as View, Edit, and Delete.",
          "The listing page contains the following information:",
          {
            list: [
              "Sr. No. – Displays the serial number of each accident record for easy identification.",
              "Vehicle Name – Shows the name of the vehicle involved in the accident.",
              "Location – Displays the accident location where the incident occurred.",
              "Description – Provides a brief summary or explanation of the accident.",
              "Expenses – Shows the total expense incurred due to the accident, including repair or related costs.",
              "Date – Displays the date on which the accident occurred.",
            ],
          },
          "Actions: You can perform the following actions for each accident record:",
          "View – Open the complete accident record and review all accident details.",
          "Edit – Modify existing accident information if corrections or updates are required.",
          "Delete – Remove the accident record from the system.",
        ],
      },
      {
        title: "Search and Filter Options",
        body: [
          "The Vehicle Accident module provides multiple filtering options to help you quickly locate accident records.",
          "Vehicle Name Filter: Allows you to search accident records based on the selected vehicle.",
          "Location / Description Search: Search accident entries using accident location or description keywords.",
          "Date Filter: Filter accident records based on a specific accident date.",
          "Period Filter: You can filter records by All Records, Monthly Records, or Yearly Records. These filters help you quickly analyze accident history over different periods.",
        ],
      },
      {
        title: "Adding a New Accident",
        body: [
          "To create a new accident record, click the Add Accident button located on the top-right side of the Vehicle Accident page. This opens the New Vehicle Incident Details form where accident information can be entered.",
          "Vehicle Name: Select the vehicle involved in the accident from the available vehicle list.",
          "Location: Enter the exact location where the accident occurred.",
          "Total Expense: Enter the total amount spent due to the accident, including repairs, recovery charges, maintenance costs, or other related expenses.",
          "Driver: Select the driver who was operating the vehicle during the incident.",
          "Date: Select the accident date using the calendar picker.",
          "Description: Provide a detailed explanation of the accident, including incident circumstances, damage details, observations, or additional notes.",
          "Accident Image / Supporting Document: Upload accident-related files such as accident photographs, damage images, insurance documents, police reports, invoices, repair bills, and supporting evidence. These documents help maintain a complete accident record.",
          "Submit: Saves the accident information and creates a new accident record. Cancel: Discards the entered information and returns to the accident listing page.",
        ],
      },
      {
        title: "Viewing Accident Details",
        body: [
          "The View Accident page provides complete information about a recorded vehicle accident. You can review the following details:",
          "Vehicle Name – Displays the vehicle involved in the accident.",
          "Location – Shows the location where the accident occurred.",
          "Driver – Displays the assigned driver associated with the incident.",
          "Expenses – Shows the total accident expense amount.",
          "Date – Displays the recorded accident date.",
          "Description – Provides detailed accident information and remarks.",
          "Uploaded Documents – You can view uploaded accident-related files, including accident images, repair invoices, insurance documents, and supporting reports. These files provide complete documentation for future reference and claim processing.",
        ],
      },
      {
        title: "Benefits of the Vehicle Accident Module",
        body: [
          "Centralized Accident Records: Maintain all accident information in one place for easy access and management.",
          "Expense Tracking: Track accident-related expenses and monitor repair costs across the fleet.",
          "Insurance Claim Support: Store invoices, images, and supporting documents required for insurance processing.",
          "Improved Vehicle Safety Monitoring: Analyze accident history to identify recurring issues and improve fleet safety.",
          "Driver Performance Analysis: Review accident records associated with drivers to improve driver training and compliance.",
          "Historical Record Maintenance: Maintain a complete history of vehicle incidents for auditing and reporting purposes.",
          "Easy Search and Reporting: Quickly locate accident records using filters, dates, vehicle names, and descriptions.",
          "By utilizing the Vehicle Accident Module, fleet managers can efficiently document incidents, track expenses, manage supporting records, and maintain complete accident histories for all vehicles within the system.",
        ],
      },
    ],
  },
  {
    route: "/VehicleAccesory",
    sections: [
      {
        title: "Adding a Vehicle Accessory",
        body: [
          "To add a new vehicle accessory, click on the Add Accessory button located on the right side of the screen. This will open a form where you can enter the accessory details.",
          "When adding a vehicle accessory, you will need to provide the following details:",
          "Vehicle Name: Select the vehicle for which the accessory is being purchased or assigned.",
          "Accessory Item: Enter the name of the accessory item.",
          "Quantity: Specify the quantity of accessories purchased.",
          "Price: Enter the purchase cost of the accessory item.",
          "Purchase Date: Select the date on which the accessory was purchased.",
          "Supporting Documents: Upload invoices, bills, warranty documents, or any related files for future reference.",
          "Once all required details are entered, click the Submit button to save the accessory record.",
          "By utilizing these accessory management features, you can efficiently track vehicle accessories, monitor expenses, maintain purchase records, and improve fleet asset management.",
        ],
      },
      {
        title: "Vehicle Accessory Listings",
        body: [
          "The Vehicle Accessory Module allows you to manage and maintain records of accessories purchased for vehicles. It helps fleet managers track accessory items, purchase quantities, costs, and purchase dates for each vehicle. This ensures proper monitoring of vehicle-related equipment and expenses.",
          "The Vehicle Accessory listing page displays all accessory records associated with vehicles. You can search, filter, and manage accessory records easily.",
          "The listing contains the following information:",
          {
            list: [
              "Sr. No. – Displays the serial number of the accessory record.",
              "Vehicle Name – Displays the vehicle associated with the accessory.",
              "Accessory Item – Displays the name of the purchased accessory.",
              "Quantity – Shows the number of accessory items purchased.",
              "Price – Displays the cost of the accessory item.",
              "Purchase Date – Shows the date on which the accessory was purchased.",
            ],
          },
          "Actions:",
          "View: Click the View icon to see complete accessory details, including vehicle information, accessory item details, quantity, price, purchase date, and uploaded documents.",
          "Edit: Click the Edit icon to update accessory information such as accessory name, quantity, price, purchase date, or supporting documents.",
          "Delete: Click the Delete icon to remove an accessory record from the system.",
        ],
      },
      {
        title: "Search and Filter Options",
        body: [
          "Vehicle Name: Search accessory records by selecting a specific vehicle.",
          "Accessory Item: Search records using the accessory item name.",
          "Date: Filter records based on the accessory purchase date.",
          "Period Filter: You can view records based on All Records, Monthly Records, or Yearly Records.",
          "These filters help you quickly locate accessory records and analyze accessory purchases across the fleet.",
          "By using the Vehicle Accessory Module, fleet managers can maintain accurate accessory records, track purchase expenses, manage supporting documents, and ensure proper documentation of all vehicle accessories within the system.",
        ],
      },
    ],
  },
  {
    route: "/VehicleSpareParts",
    sections: [
      {
        title: "Vehicle Spare Parts",
        body: [
          "The Vehicle Spare Parts module helps fleet managers track, manage, and maintain records of spare parts purchased and used for vehicles. This module allows you to store spare part details, monitor warranty information, track quantities, manage purchase records, and maintain a complete spare parts inventory for every vehicle.",
          "By using the Vehicle Spare Parts module, organizations can ensure proper inventory management, reduce maintenance delays, monitor spare part costs, and maintain accurate service records for each vehicle.",
        ],
      },
      {
        title: "Adding a New Spare Part",
        body: [
          "To add a new spare part, click on the Add Spare Part button located on the top-right side of the Vehicle Spare Parts screen. This will open the Add Vehicle Spare Parts form where you can enter the spare part details.",
          "When adding a spare part, you will need to provide the following information:",
          "Vehicle Name: Select the vehicle for which the spare part is being purchased or assigned.",
          "Spare Part Name: Enter the name of the spare part. Examples: Brake Pad, Engine Oil Filter, Headlight, Clutch Plate, Battery.",
          "Quantity: Enter the quantity of spare parts purchased or available.",
          "Price: Enter the total amount or cost of the spare part.",
          "Warranty: Enter the warranty period or warranty details applicable to the spare part.",
          "Purchase Date: Select the date on which the spare part was purchased.",
          "Expiry Date: Select the expiry date if applicable for the spare part.",
          "Invoice/Document Upload: Upload supporting documents such as Purchase Invoice, Warranty Card, Supplier Receipt, and Spare Part Documentation.",
          "Submit or Cancel: After entering all required information, click Submit to save the spare part record, or click Cancel to discard the information and return to the Spare Parts listing page.",
        ],
      },
      {
        title: "Vehicle Spare Parts Listing",
        body: [
          "The Vehicle Spare Parts listing page displays all spare parts records added to the system. You can search, filter, view, edit, delete, and export spare part records directly from this screen.",
          "Each spare part record displays important information including:",
          {
            list: [
              "Sr. No. – Unique serial number assigned to the spare part record.",
              "Vehicle Name – Displays the vehicle associated with the spare part.",
              "Spare Part – Displays the spare part name.",
              "Warranty – Displays warranty information associated with the spare part.",
              "Quantity – Displays the quantity purchased or available.",
              "Amount – Displays the cost or purchase amount of the spare part.",
              "Purchase Date – Displays the date on which the spare part was purchased.",
            ],
          },
        ],
      },
      {
        title: "Search and Filter Options",
        body: [
          "The top section of the screen provides multiple filters to quickly locate spare part records.",
          "Vehicle Name Filter: Use the Vehicle Name dropdown to display spare parts associated with a specific vehicle.",
          "Spare Parts Search: Search spare parts using Spare Part Name, Warranty Information, or Quantity Details.",
          "Calendar Filter: Select a specific date to view spare part records for that date.",
          "Period Filter: Use the period filter to view records based on All Records, Monthly Records, or Yearly Records. These filters help you quickly locate historical and current spare part purchases.",
        ],
      },
      {
        title: "Actions Available",
        body: [
          "Each spare part record includes action buttons for managing the entry.",
          "View (Eye Icon): Click the Eye icon to open the complete spare part details page. The View page displays Vehicle Name, Spare Part Name, Quantity, Price, Purchase Source/Amount, Purchase Date, Expiry Date, and Uploaded Invoice or Supporting Document. This page provides a detailed overview of the selected spare part record.",
          "Edit (Pencil Icon): Click the Pencil icon to modify existing spare part information. You can update Vehicle Name, Spare Part Name, Quantity, Price, Warranty Details, Purchase Date, Expiry Date, and Uploaded Documents. After making changes, save the record to update the information.",
          "Delete (Trash Icon): Click the Delete icon to permanently remove a spare part record from the system. Deleting a record removes Spare Part Information, Warranty Details, Quantity Records, Purchase History, and Uploaded Documents associated with the record. Use this option carefully as deleted records may not be recoverable.",
        ],
      },
      {
        title: "Export Functionality",
        body: [
          "The Vehicle Spare Parts module provides an Export option that allows you to download spare part records for reporting and analysis purposes.",
          "Exported reports can be used for Inventory Management, Maintenance Planning, Warranty Tracking, Purchase Analysis, and Audit and Compliance Reporting.",
        ],
      },
    ],
  },
  {
    route: "/VehicleFine",
    sections: [
      {
        title: "Adding a Fine",
        body: [
          "To add a new vehicle fine, click on the Add Fine button located on the right side of the screen. This will open a form where you can enter the fine details.",
          "When adding a vehicle fine, you will need to provide the following details:",
          "Vehicle Name: Select the vehicle against which the fine has been issued.",
          "Fine Reason: Enter the reason for the fine, such as over-speeding, improper parking, traffic violation, toll violation, or any other applicable offense.",
          "Fine Amount: Enter the monetary penalty amount imposed on the vehicle.",
          "Fine Status: Select the current status of the fine, such as Pending, Paid, Under Review, or Cancelled.",
          "Date: Select the date on which the fine was issued or recorded.",
          "Description: Enter additional information related to the fine or violation if required.",
          "By utilizing the Vehicle Fine module, you can efficiently record, monitor, and manage all vehicle-related fines within the system. This helps maintain compliance records, track pending penalties, and ensure proper documentation of vehicle violations. If you have any questions or need further assistance, please don't hesitate to contact our support team.",
        ],
      },
      {
        title: "Vehicle Fine Listings",
        body: [
          "You can view all vehicle fine records from the Vehicle Fine listing page. The module provides multiple filters and search options to help you quickly locate specific fine records.",
          "Vehicle Name Filter: Use the Vehicle Name dropdown to search and display fines associated with a specific vehicle.",
          "Reason and Status Filter: Search fines using the violation reason or filter records based on their current status.",
          "Date Filter: Use the calendar option to view fine records for a specific date.",
          "All Section: The All dropdown allows you to view records based on All Records, Monthly Records, or Yearly Records.",
        ],
      },
      {
        title: "Vehicle Fine Information",
        body: [
          "Each fine record displays the following information:",
          {
            list: [
              "Sr. No. – Unique serial number assigned to the fine record.",
              "Vehicle Name – Displays the name of the vehicle for which the fine was issued.",
              "Reason – Displays the cause or violation that resulted in the fine.",
              "Status – Displays the current status of the fine.",
              "Fine Amount – Displays the penalty amount imposed.",
              "Date – Displays the date of the fine.",
            ],
          },
          "Actions: Each record provides the following actions:",
          "View (Eye Icon): View complete fine details.",
          "Edit (Pencil Icon): Modify existing fine information.",
          "Delete (Trash Icon): Remove the fine record from the system.",
          "Navigation: Back Button navigates to previous records; Next Button navigates to the next set of records.",
          "These features allow fleet managers and administrators to maintain accurate records of vehicle fines, monitor payment statuses, and efficiently manage compliance-related expenses.",
        ],
      },
    ],
  },
  {
    route: "/VehicleMaintenance",
    sections: [
      {
        title: "Vehicle Maintenance Dashboard",
        body: [
          "The Vehicle Maintenance dashboard provides a centralized view of all maintenance records and their current status.",
          "Viewing Vehicle Maintenance Records: Navigate to Vehicle Expenses → Vehicle Maintenance. The screen displays all vehicle maintenance records categorized into different sections:",
          {
            list: [
              "All – Displays all maintenance records.",
              "Dues – Displays services that are due or overdue.",
              "Upcoming – Displays upcoming scheduled services.",
              "History – Displays completed maintenance records.",
            ],
          },
          "You can switch between tabs to quickly view maintenance activities based on their status.",
        ],
      },
      {
        title: "Filter Options",
        body: [
          "The top section provides multiple filters to locate specific maintenance records.",
          "Vehicle Name: Select a specific vehicle to view maintenance records associated with that vehicle.",
          "Kilometers: Filter maintenance records based on vehicle kilometers.",
          "Date: Select a maintenance date to view records for a specific day.",
          "Status Filter: Allows you to filter records based on maintenance status.",
        ],
      },
      {
        title: "Navigation Controls",
        body: [
          "Back Button: Moves to previous maintenance records or previous date range.",
          "Next Button: Moves to the next set of maintenance records or date range.",
          "Add New: Used to create a new vehicle maintenance schedule.",
        ],
      },
      {
        title: "Maintenance Record Details",
        body: [
          "Each maintenance record contains the following information:",
          {
            list: [
              "Sr. No. – Unique sequence number assigned to the maintenance record.",
              "Vehicle Name – Displays the vehicle associated with the maintenance schedule.",
              "Service Date – Shows the date on which the maintenance service was scheduled or performed.",
              "Kilometers Driven – Displays the vehicle's kilometer reading at the time of service.",
              "Due Date – Indicates the next scheduled maintenance due date.",
              "Service Cost – Displays the cost incurred for the maintenance activity.",
              "Action – Provides management options: View Maintenance Details and Edit Maintenance Information.",
            ],
          },
          "Key Features: View all maintenance records, track due and upcoming services, monitor maintenance expenses, quickly search maintenance schedules, access complete maintenance history, and create and manage service reminders.",
        ],
      },
      {
        title: "Add Vehicle Maintenance",
        body: [
          "The Add Vehicle Maintenance screen is used to create a new maintenance schedule for a vehicle.",
          "Vehicle Name: Select the vehicle for which maintenance needs to be scheduled.",
          "Service Task: Enter the maintenance activity to be performed. Examples: Oil Change, Engine Service, Brake Inspection, Tire Replacement, General Maintenance.",
          "Kilometers Driven: Enter the current odometer reading of the vehicle.",
          "Next Service Kilometers: Specify the kilometer reading at which the next service should be performed.",
          "Service Mode: Determines how the next service will be calculated. Automatic – the system automatically calculates future service schedules. Manual – the user manually manages future service schedules.",
          "Next Service Duration: Specify the maintenance interval duration. Examples: 30 Days, 60 Days, 90 Days.",
          "Service Date: Select the date on which the current maintenance is performed.",
          "Next Service Date: Select the date when the next service should occur.",
        ],
      },
      {
        title: "Service Settings",
        body: [
          "This section controls maintenance notifications and reminders.",
          "Send Service Notification: Determines whether maintenance reminders should be sent. Yes – the system sends maintenance notifications. No – no maintenance reminders will be generated.",
          "Notify Days Before Service: Specify how many days before the service date the system should notify you. Example: 5 Days, 7 Days, 15 Days.",
          "Notifications In A Day: Specify the number of reminders to send per day. Example: 1 Notification, 2 Notifications, 3 Notifications.",
          "Notes / Description: The large text area allows you to enter additional maintenance information such as service observations, repair notes, workshop details, and maintenance comments.",
        ],
      },
      {
        title: "View Vehicle Maintenance",
        body: [
          "The View Vehicle Maintenance screen displays complete information about a maintenance record.",
          "Vehicle Name: Displays the vehicle associated with the maintenance record.",
          "Last Service On: Shows the date when the last service was completed.",
          "Kilometers Driven: Displays the odometer reading recorded during service.",
          "Next Service Kilometers: Shows the kilometer target for the next maintenance.",
          "Next Service On: Displays the next scheduled maintenance date.",
          "Service Cost: Shows the total maintenance expense.",
          "Service Task: Displays the maintenance activity performed.",
          "Service Mode: Indicates whether the service schedule is Automatic or Manual.",
          "Service Completion Type: Displays the service status. Examples: Completed, Due, Pending.",
          "Service Notification: Shows notification settings configured for the service schedule.",
        ],
      },
    ],
  },
  {
    route: "/FuelExpenses",
    sections: [
      {
        title: "Vehicle Fuel Expenses",
        body: [
          "The Vehicle Fuel Expenses module allows you to record, track, and manage fuel-related expenses for vehicles within the fleet. This module helps fleet managers monitor fuel consumption, analyze operating costs, maintain fuel purchase records, and improve budgeting and expense control.",
        ],
      },
      {
        title: "Adding a Fuel Expense Record",
        body: [
          "To add a new fuel expense record, click on the + Fuel Expenses button located on the right side of the screen. This will open a form where you can enter the fuel expense details.",
          "When adding a fuel expense record, you will need to provide the following details:",
          "Vehicle Number: Select or enter the vehicle number for which the fuel expense is being recorded.",
          "Current Odometer: Enter the vehicle's current odometer reading at the time of fuel purchase.",
          "Quantity: Enter the quantity of fuel added to the vehicle.",
          "Amount: Enter the total cost of the fuel purchase.",
          "Date: Select the date on which the fuel expense occurred.",
          "Once all required information is entered, click the Save button to create the fuel expense record.",
          "By utilizing these fuel expense management features, organizations can effectively monitor fuel consumption, track operating expenses, maintain accurate fuel records, and improve fleet cost management. If you have any questions or need further assistance, please don't hesitate to contact our support team.",
        ],
      },
      {
        title: "Fuel Expense Listings",
        body: [
          "The Fuel Expense listing page displays all fuel transactions recorded within the system. You can review fuel purchases, monitor fuel costs, and manage fuel expense records for all vehicles.",
          "The listing provides important fuel-related information for each transaction and allows you to quickly access, update, or remove records when required.",
          "Search Functionality: The search option allows you to quickly locate fuel expense records based on vehicle information and other available criteria. This helps you efficiently find specific fuel transactions without manually reviewing all records.",
        ],
      },
      {
        title: "Fuel Expense Information",
        body: [
          "Each fuel expense record displays the following information:",
          {
            list: [
              "Vehicle Number – Displays the unique vehicle identifier associated with the fuel transaction.",
              "Current Odometer – Displays the odometer reading recorded at the time of fuel purchase.",
              "Quantity – Displays the amount of fuel added to the vehicle.",
              "Amount – Displays the total fuel purchase cost.",
              "Date – Displays the date on which the fuel transaction was recorded.",
            ],
          },
          "Actions: Each fuel expense record includes the following actions:",
          "View: Allows you to review complete fuel expense information for the selected record.",
          "Edit: Allows you to modify existing fuel expense details such as odometer reading, quantity, amount, or date.",
          "Delete: Allows you to remove fuel expense records that are no longer required.",
        ],
      },
      {
        title: "Tracking, Monitoring & Reporting",
        body: [
          "Fuel Usage Tracking: The Fuel Expenses module helps organizations maintain accurate fuel usage records for each vehicle. By recording odometer readings, fuel quantities, and costs, you can monitor fuel consumption patterns and identify trends that may impact operational efficiency.",
          "Expense Monitoring: The module provides visibility into fuel-related expenses across the fleet. This information assists fleet managers in controlling operational costs, monitoring vehicle performance, and improving budget planning.",
          "Reporting and Analysis: Fuel expense records can be used for operational analysis, financial reporting, budgeting, auditing, and reimbursement purposes. Maintaining accurate fuel transaction records helps organizations make informed decisions regarding fleet operations and fuel management.",
          "By utilizing the Vehicle Fuel Expenses module, fleet managers, finance teams, and vehicle operators can efficiently track fuel purchases, monitor fuel consumption, maintain expense records, and improve overall fleet cost management.",
        ],
      },
    ],
  },

  // ===================== COMMUNICATION =====================
  {
    route: "/Announcement",
    sections: [
      {
        title: "Adding an Announcement",
        body: [
          "To add a new announcement, click on the + Add Announcement button located on the screen. This will open a form where you can enter the announcement details.",
          "When adding an announcement, you will need to provide the following details:",
          "Announcement Information: Enter the title and description of the announcement.",
          "Announcement Type: Select the type of announcement from the dropdown (e.g., General, Emergency, Reminder, etc.).",
          "Announcement Date: Select the date for the announcement.",
          "Content Type: Choose the format of the announcement such as Text, Image, or other media types.",
          "Media Upload: Optionally upload an image for the announcement.",
          "After entering all the details, click Submit to publish the announcement or Cancel to discard changes.",
        ],
      },
      {
        title: "Viewing Announcements",
        body: [
          "You can view announcements displayed in card format.",
          "Each announcement card shows the Title, Date, and Image (if uploaded).",
          "You can scroll through announcements or use pagination to view more announcements.",
        ],
      },
      {
        title: "Searching & Filtering",
        body: [
          "You can search and filter announcements using available options. You can search announcements by title using the search bar, filter announcements by date using the date picker, and click Clear Filter to reset filters.",
        ],
      },
      {
        title: "Editing & Deleting Announcements",
        body: [
          "Each announcement card has a three-dot menu for actions. From the menu, you can edit the announcement details or delete the announcement.",
        ],
      },
    ],
  },
  {
    route: "/Email",
    sections: [
      {
        title: "Email Listing Screen",
        body: [
          "In the Email listing screen, you can view all the sent and received emails in a structured list format.",
          "Each email record displays Sr. No, Administrator Name, Subject, and Action (Eye Icon).",
          "You can use the search and filter options available on this screen to quickly find specific emails. From this screen you can also search emails by Administrator Name, search emails by Subject, apply filters to refine email results, and click Export button to download email data.",
          "To view full email details, click on the Eye icon in the Action column.",
        ],
      },
      {
        title: "Sending an Email",
        body: [
          "To send a new email, click on the + button located on the Email screen. This will open the email compose form where you can enter the email details.",
          "When sending an email, you will need to provide the following details:",
          "Recipient Role: Select the recipient role such as Administrator, Transport Manager, Driver, Fleet Manager, Vehicle Assistant, or Delivery Person.",
          "Administrator Name: Enter the administrator name.",
          "Subject: Enter the subject of the email.",
          "Message: Enter the email content in the message box.",
          "After entering all details, click Submit to send the email or click Cancel to discard the email.",
        ],
      },
      {
        title: "Viewing Email Details Screen",
        body: [
          "When you click on the Eye icon from the email listing screen, you can view full email details.",
          "This screen displays the Administrator Name, Subject, and Full Message Content. This helps you read complete communication details of the selected email.",
        ],
      },
      {
        title: "Compose Email Screen",
        body: [
          "In the Compose Email screen, administrators can send emails to different user roles.",
          "This screen includes the following fields:",
          {
            list: [
              "Recipient Dropdown: Select the user role or recipient",
              "Subject Field: Enter the email subject",
              "Message Box: Enter the email content",
            ],
          },
          "After filling the details, click Send to send email or click Cancel to discard email.",
        ],
      },
      {
        title: "Searching & Filtering Emails",
        body: [
          "On the Email listing screen, you can search and filter emails. You can search using Administrator Name, search using Subject, apply filters to narrow down email results, and click Clear Filter to reset filters.",
        ],
      },
      {
        title: "Export Emails",
        body: [
          "You can export email data from the Email listing screen. To export, click on the Export button and download email records for reporting or reference.",
        ],
      },
      {
        title: "Key Features",
        body: [
          "The Email module includes the following features:",
          "Role-Based Messaging: You can send emails to different user roles such as Administrator, Transport Manager, Driver, Fleet Manager, Vehicle Assistant, and Delivery Person.",
          "Email Listing Management: The email listing screen allows you to view all emails in list format, access email details using the Eye icon, and search and filter emails easily.",
          "Email Actions: Each email has an action option — the Eye Icon to view full email details.",
          "Email Composition: The + button opens the email compose screen where you can create new emails.",
          "Export Function: The Export button allows you to download the email list and use data for reporting.",
        ],
      },
    ],
  },
  {
    route: "/PushNotification",
    sections: [
      {
        title: "Adding & Sending Push Notification",
        body: [
          "To add a new push notification, click on the Add Notification button located on the screen. This will open the notification form where you can enter the notification details.",
          "When adding a push notification, you will need to provide the following details:",
          "Recipient Role: Select the user role such as Driver, Transport Manager, Fleet Manager, Vehicle Assistant, Administrator, or Delivery Person.",
          "Message: Enter the notification message in the text field.",
          "After entering the details, click Send to instantly send the notification to selected users, or click Cancel to discard the notification.",
          "Once sent, users will receive the notification on their devices in real time.",
        ],
      },
      {
        title: "Viewing & Managing Notifications",
        body: [
          "You can view all sent notifications in the Push Notification listing screen.",
          "Each notification displays the Message content, Recipient role, and Date and time of sending.",
          "You can scroll through the list to view notifications, view notification history, and track all previously sent messages.",
        ],
      },
      {
        title: "Searching & Filtering Notifications",
        body: [
          "You can search and filter notifications using available options on the listing screen. You can search notifications by role, search notifications by message or keyword, filter notifications by date, and click clear filter to reset the results.",
          "You can also export notification data for reporting or record keeping.",
        ],
      },
    ],
  },
  {
    route: "/MyMessages",
    sections: [
      {
        title: "My Messages / Chat Listing Screen",
        body: [
          "In the My Messages screen, you can view all individual and group conversations.",
          "From this screen, you can view existing chats and group chats, open a chat to view messages, view message timestamps and status (sent, delivered, read), use the search bar to find users, messages, or groups, and filter chats by individual or group conversations.",
          "Message status indicators: Single tick — Message sent; Double tick — Message delivered; Double tick bold — Message read.",
          "You can also use @mention in messages to notify specific users.",
        ],
      },
      {
        title: "Sending Messages & Starting Chat",
        body: [
          "To send a message or start a new chat, click on the New Chat option.",
          "You can select an existing user or contact, type your message in the input field, and click Send to dispatch the message.",
          "You can also start a new conversation directly from the contact list and continue messaging in real time.",
        ],
      },
      {
        title: "Group Chats",
        body: [
          "To create or manage group chats, go to the Group tab or click New Group.",
          "When creating a group, you will need to provide the following details:",
          {
            list: [
              "Group Name: Enter the name of the group",
              "Members: Select multiple users from the contact list",
            ],
          },
          "After entering details, click Submit to create the group or Cancel to discard.",
          "Once the group is created, you can open group chat and send messages, add or remove participants (admin control), exit the group if needed, and use @mention to notify specific members. You can also search group messages using the search bar.",
        ],
      },
      {
        title: "Managing Chats & Searching",
        body: [
          "You can manage and search chats from the listing screen. You can search messages, users, or groups using the search bar, filter between ALL chats and Group chats, view chat history for reference, track message delivery and read status, and scroll through previous conversations.",
          "Notifications are triggered for new messages and mentions.",
        ],
      },
    ],
  },
  {
    route: "/TeamMessages",
    sections: [
      {
        title: "Team Messages Listing Screen",
        body: [
          "In the Team Messages screen, you can view all available teams and their conversations.",
          "From this screen, you can view all created teams in the left panel, select any team to view messages, see real-time chat updates within each team, identify online members using status indicators (green dot), and scroll through previous team conversations.",
          "Green dot indicator shows that a team member is currently online and available for communication. You can also receive notifications for new messages sent within the team.",
        ],
      },
      {
        title: "Creating & Managing Teams",
        body: [
          "To create a new team, click on the + Create Teams button.",
          "When creating a team, you will need to provide the following details:",
          {
            list: [
              "Team Name: Enter a name for the team",
              "Members: Select users to add to the team",
            ],
          },
          "After entering the details, click Submit to create the team or Cancel to discard the creation.",
          "Once the team is created, it will appear in the team list on the left panel.",
          "You can manage existing teams by clicking the three-dot menu next to each team, where you can rename the team or delete the team.",
        ],
      },
      {
        title: "Sending Messages (Compose Message)",
        body: [
          "To send a message within a team, open the Compose Message option.",
          "You can select a user from the dropdown list, enter the team name if required, type your message in the message field, click Submit to send the message, or click Cancel to discard the message.",
          "Once sent, the message will be delivered instantly to the selected team or user, and will appear in the respective team chat window. Recipients will receive real-time notifications for new messages.",
        ],
      },
      {
        title: "Managing Team Communication",
        body: [
          "You can manage and track all team conversations using the available options in the module. You can switch between different teams easily, view real-time message updates, track active users in each team, maintain structured communication based on roles, and receive alerts for new messages and updates.",
          "This ensures smooth coordination between all operational roles within the system.",
        ],
      },
    ],
  },

  // ===================== REPLAY / PLAYBACK =====================
  {
    route: "/ReplayPlayback",
    sections: [
      {
        title: "Replay & Playbacks",
        body: [
          "The Replay & Playbacks module allows you to review and analyze the historical movement of vehicles over a selected period of time. This module helps fleet managers, administrators, and operators visualize past trips on an interactive map, verify routes taken by vehicles, monitor driver behavior, investigate incidents, and generate reports for analysis.",
          "By utilizing the Replay & Playbacks module, you can gain complete visibility into vehicle journeys, improve route planning, ensure driver compliance, and maintain accurate historical tracking records.",
        ],
      },
      {
        title: "Viewing Vehicle Playback",
        body: [
          "To view the historical movement of a vehicle, navigate to the Replay & Playbacks module from the main menu. This will open the playback screen where you can select a vehicle, date, and time range to review vehicle activity.",
          "When viewing a playback, you will need to provide the following details:",
          "Vehicle: Select the vehicle whose historical movement you want to review.",
          "Date: Select the date for which you want to view the playback. You can use the calendar option to choose a specific date.",
          "Start Time: Enter or select the start time from which the playback should begin.",
          "End Time: Enter or select the end time until which the playback should continue.",
          "Submit: Click the Submit button to load the historical tracking data for the selected vehicle and time period.",
          "Once the data is loaded, the vehicle route will be displayed on the map and playback controls will become available.",
          "By utilizing these playback features, you can efficiently monitor vehicle history, verify completed trips, investigate route deviations, and analyze fleet performance.",
        ],
      },
      {
        title: "Playback Features",
        body: [
          "The Replay & Playbacks module provides several features to help you analyze vehicle movements effectively.",
          "Historical Route Playback: View the complete historical route traveled by the selected vehicle during the specified time period.",
          "Interactive Map: The vehicle journey is displayed on an interactive map, allowing you to easily visualize movement patterns and route history.",
          "Vehicle Status Monitoring: During playback, you can view vehicle activity such as:",
          { list: ["Running", "Parked", "Idle", "Untracked"] },
          "Route Visualization: Different route colors help identify vehicle movement information:",
          {
            list: [
              "Green Route – Actual route traveled by the vehicle.",
              "Red Route – Planned or predefined route.",
              "Yellow Route – Areas where over-speeding occurred.",
            ],
          },
          "Trip Information Card: The playback screen may display additional information including Driver Name, Driver Photograph, Vehicle Number, Start Time, End Time, Current Speed, and Vehicle Status.",
        ],
      },
      {
        title: "Playback Controls",
        body: [
          "You can control playback using the available options:",
          "Play: Start the vehicle movement animation.",
          "Pause: Temporarily stop playback and resume when required.",
          "Move Forward: Advance the playback to review upcoming vehicle movements.",
          "Move Backward: Return to previous playback positions.",
          "Increase Speed: Speed up the playback animation.",
          "Decrease Speed: Reduce the playback speed for detailed analysis.",
          "Reset: Restart playback from the beginning of the selected route.",
        ],
      },
      {
        title: "Map Controls",
        body: [
          "The map provides additional navigation controls:",
          "Zoom In (+): View route details more closely.",
          "Zoom Out (-): View a larger geographical area.",
          "Full Screen: Expand the map for better visibility and route analysis.",
        ],
      },
      {
        title: "Vehicle Listings & Filters",
        body: [
          "Vehicle Listings: The Vehicle dropdown displays all available vehicles within the system. You can scroll through the list and select the vehicle whose historical tracking information you wish to review.",
          "Date and Time Filters: You can narrow playback results using Specific Date Selection, Start Time, and End Time. These filters help you retrieve precise historical tracking information.",
        ],
      },
      {
        title: "Export Options",
        body: [
          "The Replay & Playbacks module provides export functionality for reporting and record-keeping purposes.",
          "Download PDF: Export playback information in PDF format for documentation and reporting.",
          "Download Excel: Export playback data in Excel format for analysis and record management.",
        ],
      },
      {
        title: "Search and Analysis",
        body: [
          "The module allows you to analyze historical vehicle activity for Route Verification, Driver Performance Monitoring, Over-Speeding Investigation, Trip Validation, Customer Complaint Investigation, Delivery Verification, Service Confirmation, Accident Investigation, and Fleet Performance Analysis.",
        ],
      },
      {
        title: "Common Uses",
        body: [
          "Route Compliance Verification: Confirm whether drivers followed assigned routes.",
          "Trip Validation: Verify that a vehicle completed a scheduled trip.",
          "Driver Behavior Monitoring: Review over-speeding events and route deviations.",
          "Accident Investigation: Analyze vehicle movement before and after an incident.",
          "Proof of Service: Confirm vehicle presence at a customer location during a specified period.",
          "Fuel and Route Optimization: Review historical trips to identify more efficient routes and reduce operating costs.",
        ],
      },
      {
        title: "Expected Results",
        body: [
          "After selecting a vehicle, date, and valid time range, the system will display:",
          {
            list: [
              "Vehicle route history on the map.",
              "Vehicle movement playback.",
              "Trip details and vehicle status information.",
              "Route visualization with movement indicators.",
              "Playback controls for route analysis.",
              "Export options for reporting purposes.",
            ],
          },
          "If no tracking data is available for the selected period, the system will display an appropriate message indicating that no records were found.",
          "By utilizing the Replay & Playbacks module, fleet managers can efficiently review vehicle history, verify completed trips, investigate incidents, monitor driver performance, and generate reports for operational analysis and decision-making.",
        ],
      },
    ],
  },

  // ===================== MARKETPLACE =====================
  {
    route: "/OfferVehicleMarketPlace",
    sections: [
      {
        title: "All Vehicles Screen",
        body: [
          "In the All Vehicles screen, you can view all vehicles registered under your account in the marketplace.",
          "From this screen, you can view a complete list of all registered vehicles, see vehicle details such as Vehicle Number, Driver Name, IMEI Number, Vehicle Type, and Vehicle Capacity, enable or disable vehicles for marketplace visibility, and search vehicles using Vehicle Number, Vehicle Type, Vehicle Capacity, or Transportation Type.",
          "Each vehicle card displays the following information: Vehicle Number, Driver Name, IMEI Number (used for real-time tracking verification), Vehicle Type, and Vehicle Capacity.",
          "You can perform the following actions: Enable Vehicle → Makes the vehicle visible in the marketplace and available for booking requests. Disable Vehicle → Hides the vehicle from the marketplace and stops receiving booking requests.",
        ],
      },
      {
        title: "Offered Vehicles Screen",
        body: [
          "In the Offered Vehicles screen, you can view all vehicles that have been enabled from the All Vehicles section.",
          "From this screen, you can view all vehicles currently available for offers, see details of each offered vehicle including Vehicle Number, Driver Name, IMEI Number, Vehicle Type, and Vehicle Capacity, and track vehicles that are active in the marketplace.",
          "Each offered vehicle card includes Vehicle Number, Driver Name, IMEI Number, Vehicle Type, and Vehicle Capacity. This section helps you manage vehicles that are actively available for booking requests.",
        ],
      },
      {
        title: "Assigned Vehicles Screen",
        body: [
          "In the Assigned Vehicles screen, you can view all vehicles that have been assigned to specific orders or booking requests.",
          "From this screen, you can view all vehicles assigned to active bookings or orders, monitor real-time vehicle allocation, and avoid scheduling conflicts by tracking assigned vehicles.",
          "Each assigned vehicle card displays Vehicle Number, Driver Name, IMEI Number (for tracking verification), Vehicle Type, and Vehicle Capacity. This section ensures proper tracking of vehicles that are currently engaged in trips or assignments.",
        ],
      },
      {
        title: "Available Vehicles Screen",
        body: [
          "In the Available Vehicles screen, you can view all vehicles that are registered and enabled for marketplace use.",
          "From this screen, you can view all available vehicles ready for assignment or booking, monitor vehicle availability status, and block vehicles if they are not available for use.",
          "You can perform the following action: Block Vehicle → Moves the vehicle to the Blocked Vehicles section and removes it from availability.",
          "Each available vehicle card includes Vehicle Number, IMEI Number, Vehicle Type, and Vehicle Capacity.",
        ],
      },
      {
        title: "Blocked Vehicles Screen",
        body: [
          "In the Blocked Vehicles screen, you can view all vehicles that have been blocked from marketplace availability.",
          "From this screen, you can view all blocked vehicles in one place, search blocked vehicles using available filters, and track vehicles that are not available for booking or assignment.",
          "Each blocked vehicle card displays Vehicle Number, IMEI Number, Vehicle Type, and Vehicle Capacity. Blocked vehicles remain inactive until re-enabled from the available or management section.",
        ],
      },
    ],
  },
  {
    route: "/DirectOrderMarketPlace",
    sections: [
      {
        title: "Order List Screen",
        body: [
          "In the Order List screen, you can view all incoming booking requests sent by users for vehicle booking.",
          "From this screen, you can view all incoming booking requests in list format, see booking details such as pickup location, drop location, pickup date & time, drop date & time, view user details such as name, email, and contact number, view number of vehicles requested, use filter options to sort and search requests by each field, view request details using the eye icon, and accept or reject booking requests.",
          "Each order displays the following information: Pick Up Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Name, Email, Contact Number, and Action Buttons.",
        ],
      },
      {
        title: "Order Acceptance Flow",
        body: [
          "When you click on the Accept (✔) icon, the Assign Vehicle popup will open.",
          "From this popup, select the required vehicle and click Assign to confirm the vehicle allocation.",
          "Once assigned, the order moves to Order Accepted / next processing stage, and you can further proceed toward the order confirmation flow.",
        ],
      },
      {
        title: "Offer Vehicle Screen",
        body: [
          "In the Offer Vehicle screen, you can view all accepted booking requests where vehicle assignment is in progress.",
          "From this screen, you can view all accepted booking requests, see details of pickup and drop locations, view scheduled pickup and drop date & time, view customer details (name, email, contact number), track vehicles offered for a booking request, and monitor requests waiting for user confirmation.",
          "Each offer vehicle record includes Pick Up Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Name, Email, and Contact Number. This section ensures smooth handling of accepted bookings before final confirmation.",
        ],
      },
      {
        title: "Order Confirmation Screen",
        body: [
          "In the Order Confirmation screen, you can view all booking requests that are waiting for final confirmation.",
          "From this screen, you can view all confirmed booking requests pending final processing, review full booking details before completion, access request details using the eye (👁) icon, and track order status before final confirmation stage.",
          "Each order confirmation record includes Pick Up Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Name, Email, Contact Number, and Action (View Request Details).",
        ],
      },
      {
        title: "View Request Details",
        body: [
          "When you click on the eye icon, you will be redirected to the Request Details page.",
          "Here you can view Pickup Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Customer Name, Email ID, Contact Number, and Vehicle Details. This helps in reviewing complete booking information before final confirmation.",
        ],
      },
      {
        title: "My Order Screen",
        body: [
          "In the My Order screen, you can view all booking requests assigned to the logged-in vehicle owner or provider.",
          "From this screen, you can view all incoming assigned booking requests, see full trip details including pickup and drop information, view customer details such as name, email, and contact number, track number of vehicles requested, use filters to search and sort orders, and open request details using the eye icon.",
          "Each My Order record includes Pick Up Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Name, Email, Contact Number, and Action (View Request Details).",
          "Request Details View: When you click on the eye icon in My Order, you will navigate to the Request Details page where you can view Pickup Location, Drop Location, Pickup Date & Time, Drop Date & Time, Number of Vehicles, Customer Name, Email ID, Contact Number, and Vehicle Details.",
        ],
      },
    ],
  },
  {
    route: "/AcceptedRequests",
    sections: [
      {
        title: "Order Accept Listing Screen",
        body: [
          "In the Order Accept screen, you can view all booking requests that have been accepted and are ready for further processing.",
          "From this screen, you can view all accepted booking requests in a structured list, see complete booking details for each order, track orders after user confirmation from Direct Order module, monitor order progress before final completion stages, use filters to search and sort orders based on required fields, and view full order details using available action options.",
          "Each Order Accept record displays the following information: Pick Up Location, Drop Location, Pick Up Date & Time, Drop Date & Time, Number of Vehicles, Name, Email, Contact Number, and Action. You can use the filter option to search and sort data by each field for easy tracking and management.",
          "Order Processing Flow: Once an order is accepted from the Direct Order module, the request is moved to the Order Accept module, the system registers it as a confirmed booking request, you can track the order status from this section, and the order becomes eligible for further processing and confirmation steps. This ensures a smooth transition from booking request to confirmed order lifecycle.",
          "Order Management & Tracking: From the Order Accept screen, you can monitor all accepted booking requests in real time, track customer booking details and trip schedule, ensure proper handling of confirmed orders before execution, use filters to quickly locate specific accepted bookings, and maintain visibility of all ongoing accepted orders. This helps in better operational control and reduces booking management confusion.",
        ],
      },
    ],
  },
  {
    route: "/VehicleBooking2",
    sections: [
      {
        title: "Booking Request Form Screen",
        body: [
          "In the Vehicle Booking Request form screen, you can create a new booking request by entering all required trip details.",
          "When creating a booking request, you need to provide the following details:",
          {
            list: [
              "Pickup Location: Enter the exact address or landmark where the vehicle should arrive for pickup",
              "Pickup Date: Select the scheduled date for pickup",
              "Pickup Time: Select the expected pickup time",
              "Vehicle: Choose the required vehicle based on transportation needs",
              "Name: Enter your name for identification and communication",
              "Email: Enter your email address for booking confirmation and updates",
              "Drop Date: Select the date for drop-off or delivery completion",
              "Drop Time: Select the expected drop-off time",
              "Number of Vehicles: Enter how many vehicles are required for the booking",
              "Mobile Number: Enter your contact number for communication",
              "Country Code: Select the appropriate international dialing code for your number",
            ],
          },
        ],
      },
      {
        title: "Submit & Cancel Booking",
        body: [
          "After entering all required details, you can proceed with booking actions. You can click Submit to finalize and send the booking request for processing, or click Cancel to discard the entered information and return to the previous screen.",
          "Once submitted, the booking request is sent to Marketplace users, notifications are triggered to registered users via email and system alerts, and the request becomes available for acceptance or rejection in Direct Order and related modules.",
        ],
      },
      {
        title: "Booking Request Processing Flow",
        body: [
          "After submission of a booking request, the request is routed to users with Marketplace access, notifications are sent to registered email addresses, users can review and respond to the request (accept or reject), and the request moves through the booking lifecycle modules such as Direct Order, Order Accept, and Order Confirmation. This ensures a structured and transparent booking management process.",
        ],
      },
    ],
  },

  // ===================== PARKING MANAGEMENT =====================
  {
    route: "/ParkingManagement",
    sections: [
      {
        title: "Parking Station Listing Screen",
        body: [
          "In the Parking Station screen, you can view all registered parking stations along with their details.",
          "From this screen, you can view all parking stations in the system, see station details such as Station Name, Station Code, and Address, identify and manage multiple parking locations, view stations on an interactive map interface, and access suggested vehicles related to parking availability.",
          "Each station includes the following information: Station Name (parking location name), Station Code (unique identifier for the station), and Address (full physical location details). You can use the station list to manage and monitor all parking locations efficiently.",
        ],
      },
      {
        title: "Add Parking Station Screen",
        body: [
          "To add a new parking station, click on the Add Parking Station option.",
          "When adding a parking station, you will need to provide the following details:",
          {
            list: [
              "Station Name: Enter the name of the parking location",
              "Station Code: Enter a unique code for identification",
              "Address: Enter the complete physical address of the parking station",
            ],
          },
          "After entering the details, click Save to create the parking station or Cancel to discard the entered information. Once saved, the new station will be added to the system and will be available in the station list.",
        ],
      },
      {
        title: "Map View & Controls Screen",
        body: [
          "In the map view screen, you can visually manage parking stations and vehicle locations.",
          "From this screen, you can view all parking stations on the map, Zoom In (+) to view detailed location areas, Zoom Out (-) to view a wider area, resize the map using the bottom resize option, and navigate between different station locations.",
          "This map-based interface helps in better visualization of parking availability and vehicle placement.",
        ],
      },
      {
        title: "Suggested Vehicles Section",
        body: [
          "In the Suggested Vehicles section, you can view vehicles that are recommended or available for parking allocation.",
          "From this screen, you can view all suggested vehicles based on availability, match vehicles with nearby parking stations, and assist in efficient parking allocation and management. This helps in optimizing parking usage and improving vehicle placement decisions.",
        ],
      },
    ],
  },
];

// ===================== REPORTS (matched by report name on report routes) =====================
export const reportHelp = [
  {
    title: "Vehicle Running Summary Report",
    body: [
      "The Vehicle Running Summary Report is a detailed analytical report used to monitor and evaluate vehicle performance over a selected date. It provides key operational insights such as distance traveled, duty time, running time, idle time, and speed metrics.",
      "This report helps fleet managers analyze vehicle utilization, driving efficiency, and operational productivity on a daily basis.",
      "To view the Vehicle Running Summary Report: Navigate to the Reports / Vehicle Analytics section of the system. Select the date from the calendar or date selector. The system will display all vehicle performance data for the selected day.",
      "Report Navigation (Back & Next): At the top of the screen, navigation controls are available. Back Button moves to the previous date to view past vehicle performance data. Next Button moves to the next date to view future or latest available data. These controls help you quickly analyze vehicle performance across multiple days without manually selecting dates.",
      "Vehicle List Section: This section displays a tabular view of all vehicles and their performance data for the selected date. Each row represents one vehicle and includes detailed operational metrics.",
      "Table Column Explanation:",
      { list: [
          "Sr. No – Displays the serial number of each vehicle record for easy identification.",
          "Vehicle Reg No – Shows the registration number of the vehicle. This helps uniquely identify each vehicle in the fleet.",
          "Report Date – Indicates the selected date for which the report is generated.",
          "Vehicle IMEI – Displays the unique IMEI number of the tracking device installed in the vehicle.",
          "Distance (KM) – Shows the total distance traveled by the vehicle during the selected date in kilometers.",
          "Actual Duty Time (Hrs) – Represents the total time the vehicle was considered on duty or active for operations.",
          "Average Speed (Unit/Hr) – Displays the average speed of the vehicle during its running period.",
          "Max Speed (Unit/Hr) – Shows the highest speed recorded by the vehicle during the selected date.",
          "Actual Running Time (Hrs) – Indicates the total time the vehicle was in motion during the day.",
          "Idle Time (Hrs) – Represents the total time the vehicle remained stationary while still being active in the system.",
      ] },
      "Export Functionality: The report includes an Export option that allows you to download the vehicle running summary data. Export formats available: PDF (for reporting and documentation) and Excel (for analysis and data processing). This helps in maintaining records and performing fleet performance analysis outside the system.",
    ],
  },
  {
    title: "Vehicle Ignition Summary Report",
    body: [
      "When you open the Vehicle Ignition Summary Report, you will see a date-based report view with a vehicle list displaying ignition ON/OFF activity and trip details.",
      "At the top of the screen, the selected date is displayed, along with navigation controls like Back and Next to move between dates.",
      "Date Navigation: Back Button is used to view previous date vehicle ignition data, Next Button is used to view next date vehicle ignition data, and Date Display shows the currently selected report date.",
      "Vehicle List Section: The Vehicle List displays ignition summary details in a structured table format. Each row represents a vehicle's ignition activity for the selected date. The table includes the following details:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Registration number of the vehicle",
          "Date: Date of ignition activity",
          "Start Time: Time when vehicle ignition was turned ON",
          "Start Location: Location where the trip started",
          "End Time: Time when vehicle ignition was turned OFF",
          "End Location: Location where the trip ended",
          "Distance (km): Total distance covered during the trip",
          "Duration: Total time between ignition ON and OFF",
          "Average Speed (unit/hr): Average speed during the trip",
          "Max Speed (unit/hr): Highest speed recorded during the trip",
      ] },
      "Export Functionality: You can download the ignition summary report for offline use or reporting purposes. Click on the Export Button and select export format: PDF or Excel.",
      "Back and Next Navigation: Back moves to previous day report, Next moves to next day report. Helps in analyzing vehicle ignition activity across multiple dates.",
    ],
  },
  {
    title: "Vehicle Running Report",
    body: [
      "When you open the Vehicle Running Report, the system displays a vehicle-wise running summary for the selected date.",
      "At the top of the screen, the selected date is displayed along with navigation controls like Back and Next to move between different dates.",
      "Date Navigation: Back Button is used to view previous date running report data, Next Button is used to view next date running report data, and Date Display shows the currently selected report date.",
      "Vehicle List Section: The Vehicle List displays running summary details in a structured table format. Each row represents a vehicle's running performance for the selected date. The table includes the following details:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Registration number of the vehicle",
          "Date: Date of report",
          "Start Time: Time when the vehicle started moving",
          "Start Location: Location where the vehicle movement started",
          "End Time: Time when the vehicle stopped moving",
          "End Location: Location where the vehicle trip ended",
          "Distance (km): Total distance covered by the vehicle",
          "Duration: Total running time of the vehicle",
          "Average Speed (unit/hr): Average speed during movement",
          "Max Speed (unit/hr): Maximum speed recorded during the trip",
      ] },
      "Export Functionality: You can download the Vehicle Running Report for offline analysis and reporting. Click on the Export Button and select export format: PDF or Excel.",
      "Back and Next Navigation: Back moves to previous day running data, Next moves to next day running data. Helps you analyze vehicle performance across multiple dates.",
    ],
  },
  {
    title: "Vehicle Stoppage Report",
    body: [
      "When you open the Vehicle Stoppage Report, the system displays a vehicle-wise stoppage summary for the selected date.",
      "At the top of the screen, the selected date is displayed along with navigation controls like Back and Next to move between different dates.",
      "Date Navigation: Back Button is used to view previous date stoppage data, Next Button is used to view next date stoppage data, and Date Display shows the currently selected report date.",
      "Vehicle List Section: The Vehicle List displays stoppage activity details in a structured table format. Each row represents a vehicle's stoppage information for the selected date. The table includes the following details:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Registration number of the vehicle",
          "IMEI Number: Unique tracking device identifier",
          "Vehicle Reg No: Registration number of the vehicle",
          "Date: Date of stoppage report",
          "Start Time: Time when vehicle stoppage started",
          "End Time: Time when vehicle stoppage ended",
          "Duration: Total stoppage duration",
          "Location: Location where the vehicle was stopped",
      ] },
      "Export Functionality: You can download the Vehicle Stoppage Report for offline analysis and reporting purposes. Click on the Export Button and select export format: PDF or Excel.",
      "Back and Next Navigation: Back moves to previous date stoppage data, Next moves to next date stoppage data. Helps you track and analyze vehicle stoppage patterns across multiple dates.",
    ],
  },
  {
    title: "Vehicle Idle Report",
    body: [
      "The Vehicle Idle Report Module provides detailed insights into vehicles that remain stationary (idle) for a specific duration within a selected date range. This report helps fleet managers identify unnecessary idle time, improve operational efficiency, and optimize vehicle usage.",
      "It displays a date-wise summary of vehicle idle activity including duration, location, and identification details such as vehicle number and IMEI.",
      "Viewing Vehicle Idle Report: Open the Vehicle Idle Report Module from the Reports section. The system displays the report based on the selected date. By default, the current date or last selected date is shown. The report includes a structured list of all vehicles that were idle during the selected time period.",
      "Navigation Controls (Back & Next): Back Button is used to view previous date-wise idle records. Next Button is used to move forward to the next date's idle summary. These controls help you analyze idle patterns across multiple days.",
      "Export Functionality: The Export button allows you to download the idle report. Data can be exported for reporting and documentation, fleet performance analysis, and internal audits and review. Exported files typically include all visible table columns.",
      "Vehicle Idle Report Table Details:",
      { list: [
          "Sr. No – Serial number of the record for easy reference.",
          "Vehicle Number – Displays the registered vehicle number for identification.",
          "IMEI Number – Unique tracking device identifier installed in the vehicle. Used to ensure data is coming from the correct GPS device.",
          "Vehicle Reg No – Official registration number of the vehicle.",
          "Date – The specific date on which the idle activity was recorded.",
          "Start Time – The time when the vehicle started its idle state.",
          "End Time – The time when the vehicle stopped being idle or resumed movement.",
          "Duration – Total time the vehicle remained idle. Helps identify unnecessary stoppage periods.",
          "Location – Displays the geographical location where the vehicle remained idle. Useful for analyzing idle hotspots and operational inefficiencies.",
      ] },
    ],
  },
  {
    title: "Vehicle First & Last Ignition Report",
    body: [
      "The Vehicle First & Last Ignition Report Module provides a detailed summary of vehicle ignition activity for a selected time period. It shows the first time a vehicle was started (first ignition) and the last time it was turned off (last ignition) within a day, week, or month.",
      "This report helps fleet managers analyze vehicle usage patterns, operational timing, and driver activity, ensuring better fleet control and efficiency.",
      "Viewing First & Last Ignition Report: Open the module from the Reports section. By default, the system displays data for the selected date. The report shows ignition activity for all vehicles for the selected period.",
      "Time Filter (Day / Week / Month Dropdown): A dropdown filter is available at the top of the report screen. Day shows ignition details for a specific date, Week shows ignition summary for a weekly range, and Month shows ignition summary for the entire month. This helps you analyze short-term and long-term vehicle usage patterns.",
      "Navigation Controls (Back & Next): Back Button moves to the previous date/week/month based on the selected filter. Next Button moves forward to the next time period.",
      "Export Functionality: The Export button allows you to download the report data. Exported data can be used for fleet performance analysis, driver behavior monitoring, operational reporting, and management review and audits.",
      "Vehicle First & Last Ignition Table Details:",
      { list: [
          "Sr. No – Serial number for easy identification of records.",
          "Vehicle Number – Displays the registered vehicle number.",
          "IMEI Number – Unique identifier of the GPS device installed in the vehicle. Ensures data accuracy from the correct tracking device.",
          "Vehicle Reg No – Official registration number of the vehicle.",
          "Driver – Name of the assigned driver operating the vehicle.",
          "Date – The selected reporting date or period.",
          "First Ignition – Time when the vehicle was started for the first time during the selected period. Helps track start of daily operations.",
          "Last Ignition – Time when the vehicle was last turned off during the selected period. Helps understand end of operational activity.",
          "Duration – Total operational time between first ignition and last ignition. Useful for analyzing working hours and utilization.",
          "Distance (km) – Total distance traveled between first and last ignition events. Helps evaluate productivity and route efficiency.",
      ] },
    ],
  },
  {
    title: "Vehicle Activity Report",
    body: [
      "The Vehicle Activity Report is displayed in a structured table format with the following fields:",
      { list: [
          "Sr. No – Serial number for easy identification of records.",
          "Vehicle Number – Displays the registered vehicle number for identification.",
          "IMEI Number – Unique GPS tracking device identifier installed in the vehicle. Ensures data accuracy and traceability.",
          "Date – The selected reporting date for the activity record.",
          "Status – Indicates vehicle operational status during the activity period, such as Running, Idle, Stopped, or Dispatched (based on system configuration).",
          "Start Time – Time when the vehicle activity began.",
          "Start Location – Geographical location where the vehicle activity started.",
          "End Time – Time when the vehicle activity ended.",
          "End Location – Final location of the vehicle activity segment.",
          "Duration – Total time taken for the activity cycle. Helps analyze operational efficiency.",
          "Distance (km) – Total distance covered during the activity period. Useful for route and performance analysis.",
      ] },
    ],
  },
  {
    title: "Vehicle Geofence Report",
    body: [
      "Open the Vehicle Geofence Report Module from the Reports section. The system displays geofence activity based on the selected date. You can view all vehicles that triggered geofence events within the selected period.",
      "Time Filter (Day / Week / Month Dropdown): A dropdown filter is available at the top of the screen. Day shows geofence activity for a specific date, Week shows weekly geofence activity summary, and Month shows monthly geofence activity summary. This helps you analyze geofence violations and movements over different time ranges.",
      "Navigation Controls (Back & Next): Back Button moves to previous date/week/month records. Next Button moves forward to the next period of geofence activity.",
      "Export Functionality: The Export button allows you to download geofence report data. Exported reports are useful for compliance tracking, security analysis, operational reporting, and audit and review purposes.",
      "Vehicle Geofence Report Table Details:",
      { list: [
          "Sr. No – Serial number for easy identification of records.",
          "Vehicle Number – Displays the registered vehicle number.",
          "Geofence Date – The date on which the geofence event occurred.",
          "Start Time – Time when the vehicle entered or triggered the geofence zone.",
          "End Time – Time when the vehicle exited the geofence zone.",
          "End Location – The location where the vehicle exited or ended geofence activity.",
          "Geofence Name – Name of the configured geofence area (e.g., warehouse, restricted zone, depot).",
          "Duration – Total time spent inside the geofence area. Helps analyze vehicle behavior within controlled zones.",
          "Driver Name – Name of the driver operating the vehicle during geofence activity.",
      ] },
    ],
  },
  {
    title: "Vehicle Speed Report",
    body: [
      "The Vehicle List dropdown allows you to select a specific vehicle. Click the Vehicle List dropdown and select the desired vehicle; the report automatically displays speed data for the selected vehicle. This feature helps you analyze speed performance for individual vehicles.",
      "Navigation Controls: Click the Back button to navigate to the previous day's speed data. Click the Next button to navigate to the next day's speed records. This feature is useful when reviewing speed trends over consecutive days.",
      "Understanding the Vehicle Speed Chart: After selecting a vehicle, the system displays a speed graph showing vehicle speed throughout the selected date. The report heading displays Vehicle Speed Report, followed by the Selected Date and the Selected Vehicle from the Vehicle List dropdown.",
      "Chart Components: X-Axis (Time) — the horizontal axis represents time and displays the timeline of vehicle movement throughout the selected day; you can identify when the vehicle started moving, speed changes during the day, time periods of vehicle activity, and speed fluctuations over time. Y-Axis (Speed) — the vertical axis represents vehicle speed; speed values are displayed in Kilometers Per Hour (km/h). Speed Line — the graph contains a speed line that visually represents the vehicle's speed over time, helping you identify speed increases, decreases, constant speed periods, sudden acceleration, and sudden deceleration.",
      "Analyzing Vehicle Speed Data: The report can be used to identify important driving patterns. Speed Monitoring helps ensure compliance with company and regulatory speed limits. Driver Behavior Analysis reviews sudden acceleration, sudden braking, continuous speeding, and consistent driving patterns to support driver training and safety programs. Overspeed Detection helps identify periods where vehicles may have exceeded permitted speed limits so fleet managers can investigate incidents, improve driver awareness, and reduce accident risks. Route Performance Analysis reviews speed variations across different times of the day to understand traffic congestion, route efficiency, travel delays, and vehicle utilization.",
    ],
  },
  {
    title: "Live Vehicle Location Report",
    body: [
      "The report presents vehicle information in a structured tabular format, allowing you to quickly review the current status and location of vehicles. Each record represents the latest tracking update received from a vehicle.",
      "Report Fields:",
      "Sr. No. — Displays the serial number assigned to each record in the report. This helps you easily reference and identify records within the report.",
      "Vehicle Number — Displays the vehicle name or vehicle identification number configured in the system. This field helps you quickly identify the vehicle being tracked. It also displays the official vehicle registration number assigned by the transport authority, helping you verify the exact vehicle being monitored.",
      "Status — Displays the current operational status of the vehicle. Common statuses may include Running, Idle, Stopped, Parked, Offline, Ignition ON, and Ignition OFF. This helps you understand the current activity of the vehicle without needing to access detailed tracking screens.",
      "Vehicle IMEI — Displays the IMEI (International Mobile Equipment Identity) number of the GPS tracking device installed in the vehicle. The IMEI serves as a unique identifier and helps in troubleshooting, device verification, and support activities.",
      "Location — Displays the most recent location reported by the vehicle's GPS device. The location may include Street Name, Area Name, City, State, and Landmark Information. This enables you to determine the exact position of the vehicle at the time of the latest update.",
      "Last Update — Displays the date and time when the system last received tracking information from the vehicle. This helps you determine whether the vehicle is actively reporting, the freshness of tracking data, and possible communication issues if updates are delayed.",
      "Export Functionality: The report includes an Export option that allows you to download live vehicle location information for further analysis and record-keeping. Benefits include generating operational reports, sharing vehicle status with management, maintaining tracking records for audits, analyzing fleet movement data, and creating offline backups. Exported reports can typically be downloaded in supported formats such as Excel or CSV, depending on system configuration.",
    ],
  },
  {
    title: "Seat Belt Violation Report",
    body: [
      "Seat Belt Violation Report helps monitor and analyze safety compliance of drivers and vehicles. It records instances where seat belt rules were not followed, along with trip details such as time, distance, and speed.",
      "This report is useful for improving driver safety behavior and ensuring compliance with road safety regulations.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific day, or view monthly or yearly data using the dropdown. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button is used to return to the previous screen. Next Button is used to navigate forward or view additional report pages.",
      "Seat Belt Violation Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle: Vehicle involved in the violation",
          "Date: Date of the recorded trip",
          "Start Time: Trip start time",
          "End Time: Trip end time",
          "Distance (KM): Total distance covered during the trip",
          "Duration: Total duration of the trip",
          "Average Speed (Unit/HR): Average speed maintained during the trip",
          "Max Speed (Unit/HR): Highest speed recorded during the trip",
      ] },
      "Export Functionality: The report provides an Export option. You can export seat belt violation data; the exported file includes all filtered records based on selected date range. Helps in safety audits and compliance reporting.",
    ],
  },
  {
    title: "Safe Driver Ranking Report",
    body: [
      "Safe Driver Ranking Report helps evaluate and rank drivers based on their driving behavior and compliance with safety rules. It highlights violations and overall performance to identify safe and high-risk drivers. This report is useful for driver evaluation, safety improvement, and reward or penalty-based assessments.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. The report updates automatically based on the selected selection.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export safe driver ranking data; the exported file includes all filtered records based on selected date range.",
      "Safe Driver Ranking Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Driver: Name of the driver",
          "Geofence Break: Number of geofence violations",
          "Speed Exceed: Number of overspeeding events",
          "Incident: Total incidents recorded",
          "Fine: Penalty amount assigned based on violations",
      ] },
    ],
  },
  {
    title: "Vehicle Based Status Report",
    body: [
      "Vehicle Based Status Report helps you monitor vehicle activity and status in a single view. It allows you to quickly identify whether a vehicle is moving, idle, stopped, offline, or currently being tracked.",
      "This report is useful for fleet monitoring, operational control, and performance analysis of vehicles.",
      "Status Filter Dropdown: A vehicle status dropdown filter is available on the screen. You can filter vehicles based on the following statuses: Running (vehicles currently in motion), Idle (vehicles that are on but not moving), Stopped (vehicles that are not in motion and engine is off or halted), Offline (vehicles not currently sending data), and Tracked (vehicles actively being tracked in the system). Once a status is selected, the report updates automatically to show relevant vehicles only.",
      "Date Selection: At the top of the screen, a date selector is available. You can select a specific date to view vehicle status for that day. The report refreshes based on the selected date.",
      "Vehicle List Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle identification number",
          "Vehicle Registration Number: Registered number of the vehicle",
          "Vehicle IMEI: Tracking device IMEI linked with the vehicle",
          "Last Tracked: Last recorded tracking time of the vehicle",
          "Action: Available actions for the selected vehicle",
      ] },
      "Export Functionality: The report provides an Export option. You can export the vehicle status report data; the exported file contains all filtered records based on selected date and status. This helps in offline analysis and reporting.",
    ],
  },
  {
    title: "Vehicle Location Signal Report",
    body: [
      "The report displays the selected vehicle details along with all GPS signal records received from the tracking device. The Selected Vehicle Number displays the vehicle currently selected for the report, helping you verify that you are reviewing data for the correct vehicle.",
      "Report Fields — each row represents a signal record received from the vehicle tracking device:",
      "Sr. No. — Displays the serial number assigned to each record.",
      "Date — Displays the date on which the signal record was received. Helps you track historical location data and device communication records.",
      "Time — Displays the exact time when the tracking device transmitted the GPS information. The timestamp helps determine the frequency and consistency of tracking updates.",
      "GPS Data — Displays the GPS data received from the tracking device, indicating whether valid GPS information was successfully captured and transmitted. GPS data is used to determine the vehicle's location and movement.",
      "GPS Quality — Displays the quality or accuracy level of the GPS signal. A higher GPS quality generally indicates more accurate positioning, better satellite connectivity, and reliable location information. Poor GPS quality may indicate weak satellite coverage, indoor parking, tunnel movement, or device antenna issues.",
      "Latitude — Displays the latitude coordinate received from the GPS device (the north-south position).",
      "Longitude — Displays the longitude coordinate received from the GPS device (the east-west position). Together, latitude and longitude determine the exact geographical location of the vehicle.",
      "Metering Status — Displays the current metering status received from the tracking device, helping determine whether the vehicle's metering or distance measurement functionality is active and reporting correctly.",
      "Power Status — Displays the power condition of the GPS tracking device. Common statuses may include Power Connected, Power Disconnected, Battery Mode, and External Power Available. This helps you monitor device health and identify power-related issues.",
      "GSM Signal Strength — Displays the strength of the GSM network signal available to the tracking device. A strong GSM signal ensures reliable communication, timely location updates, and continuous tracking. Weak GSM signals may result in delayed tracking updates, communication interruptions, and missing location records.",
      "Export Functionality: The Vehicle Location Signal Report includes an Export option that allows you to download report data for further analysis and record keeping.",
    ],
  },
  {
    title: "Vehicle Count and Seat Capacity Report",
    body: [
      "The Vehicle Count and Seat Capacity Report provides a graphical representation of vehicles categorized by seating capacity. This report helps transport managers understand fleet distribution and identify the availability of vehicles across different capacity ranges.",
      "Upon opening the report, you will see a bar chart displaying the number of vehicles within each seating capacity category. Each bar represents a specific capacity range and displays the corresponding vehicle count.",
      "To download the chart, click the menu icon located in the upper-right corner of the chart. You can choose from the following formats: Download SVG, Download PNG, and Download CSV.",
    ],
  },
  {
    title: "Over Speeding Report",
    body: [
      "Over Speeding Report helps monitor vehicles that have crossed the configured speed limit during a selected time period. It provides insights into driving behavior, safety compliance, and route performance.",
      "This report is useful for identifying risky driving patterns and ensuring road safety compliance across the fleet.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific day, or view monthly or yearly data using the dropdown. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button is used to go to the previous screen. Next Button is used to move forward or view additional report pages.",
      "Over Speeding Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Registration number of the vehicle",
          "Driver: Driver assigned to the vehicle",
          "Date: Date of the speeding event",
          "Start Time: Time when the trip or event started",
          "End Time: Time when the trip or event ended",
          "Location: Place where the over-speeding occurred",
          "Distance (KM): Distance covered during the event",
          "Duration: Total duration of the trip/event",
          "Speed Limit: Configured speed limit for the vehicle",
          "Max Speed (Unit/HR): Highest speed recorded during the event",
          "Over Limit: Amount by which the speed exceeded the limit",
          "Percent: Percentage of overspeeding compared to allowed limit",
      ] },
      "Export Functionality: The report provides an Export option. You can export the over speeding report data; the exported file reflects the selected filters (date range and view). Useful for offline analysis and safety reporting.",
    ],
  },
  {
    title: "Immobiliser Report",
    body: [
      "Immobiliser Report helps track the activation and deactivation of vehicle immobilisers. It provides a complete history of when a vehicle was immobilised or released, helping in security monitoring and vehicle control management.",
      "This report is useful for fleet security, theft prevention tracking, and operational control.",
      "Date Selection: At the top of the screen, a date range filter (Start Date and End Date) is available. You can select a From Date and To Date. The report updates automatically based on the selected date range.",
      "Search Functionality: A Search bar is available on the screen. You can search vehicles using vehicle name or number, which helps quickly filter and find specific immobiliser records.",
      "Immobiliser Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle: Vehicle name or number",
          "Immobilisation Status: Current status (Enabled/Disabled)",
          "Enabled Date: Date when immobiliser was activated",
          "Enabled Time: Time when immobiliser was activated",
          "Disabled Date: Date when immobiliser was deactivated",
          "Disabled Time: Time when immobiliser was deactivated",
      ] },
      "Export Functionality: The report provides an Export option (Export Icon). By clicking the export icon, you can download the immobiliser report; exported data includes all filtered results based on selected date range and search. Useful for audit and security reporting.",
    ],
  },
  {
    title: "Temperature Sensor Report",
    body: [
      "Temperature Sensor Report helps monitor temperature variations recorded during vehicle movement or idle conditions. It displays multiple sensor readings along with location and running status, ensuring proper environmental and cargo condition tracking.",
      "This report is especially useful for cold chain monitoring, logistics, and sensitive goods transportation.",
      "Date Selection: At the top of the screen, a date filter option is available. You can select a specific date; a quick option like Today is also available for instant data viewing. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button is used to go to the previous screen. Next Button is used to move forward or view additional report pages.",
      "Temperature Sensor Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle: Vehicle identification number or name",
          "Date: Date of the recorded data",
          "Time: Time of the temperature reading",
          "Running Status: Vehicle status (Running/Idle/Stopped, etc.)",
          "Location: Current location of the vehicle at the time of reading",
          "Temperature-1: Reading from sensor 1",
          "Temperature-2: Reading from sensor 2",
          "Temperature-3: Reading from sensor 3",
          "Temperature-4: Reading from sensor 4",
      ] },
      "Export Functionality: The report provides an Export option. You can export temperature sensor data; the exported file includes all filtered records based on selected date. Helps in offline analysis and monitoring reports.",
    ],
  },
  {
    title: "Driver Behavior Report",
    body: [
      "Driver Behavior Report helps monitor and evaluate driver performance based on their driving patterns and rule violations. It provides a consolidated view of unsafe driving events such as speeding and geofence breaches.",
      "This report is useful for improving driver safety, reducing risky behavior, and ensuring compliance with driving standards.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch to monthly or yearly view using the dropdown. The report updates automatically based on the selected selection.",
      "Navigation Controls: Back Button is used to return to the previous screen. Next Button is used to navigate forward or view additional report pages.",
      "Driver Behavior Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Driver Image: Profile image of the driver",
          "Driver: Driver name or ID",
          "Geofence Break: Number of geofence violations",
          "Speed Exceed: Number of overspeeding incidents",
          "Incident: Total incidents recorded against the driver",
          "Fine: Penalty amount or fine assigned based on violations",
      ] },
      "Export Functionality: The report provides an Export option. You can export driver behavior data; the exported file includes all filtered records based on selected date range. Useful for performance reviews and compliance reporting.",
    ],
  },
  {
    title: "Dispatch Trip History Report",
    body: [
      "Dispatch Trip History Report helps track dispatched trips for vehicles along with driver assignment details. It provides visibility into trip initiation, vehicle usage, and driver allocation. This report is useful for fleet monitoring, trip tracking, and operational planning.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch to monthly or yearly view. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Dispatch Trip History Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Driver: Name of the assigned driver",
          "Vehicle: Vehicle used for the trip",
          "Vehicle Reg No: Registration number of the vehicle",
          "Vehicle IMEI: Tracking device IMEI linked with the vehicle",
          "Start Date: Date when the trip started",
          "Start Time: Time when the trip started",
      ] },
      "Export Functionality: The report provides an Export option. You can export dispatch trip history data; the exported file includes all filtered records based on selected date range. Useful for trip analysis and operational reporting.",
    ],
  },
  {
    title: "Dispatch Merchant Order Report",
    body: [
      "Dispatch Merchant Order Report helps track merchant-side dispatch activities. It provides visibility into order processing, dispatch status, and goods quantity for each merchant. This report is useful for monitoring merchant deliveries, order fulfillment, and dispatch tracking.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between monthly and yearly views. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to quickly find specific merchant orders. An Export option is also available; you can export dispatch merchant order data, and the exported file includes all filtered records based on selected date range.",
      "Dispatch Merchant Order Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Dispatch Merchant Name: Name of the merchant",
          "Date & Time: Date and time of order dispatch",
          "Order Number: Unique order identification number",
          "Number of Goods: Quantity of goods dispatched",
          "Dispatch Status: Current status of the dispatch order",
      ] },
    ],
  },
  {
    title: "Delivery Person Order Report",
    body: [
      "Delivery Person Order Report helps track orders handled by individual delivery personnel. It provides visibility into order assignments, delivery tracking, and dispatch status updates. This report is useful for monitoring delivery performance, order distribution, and operational tracking.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between monthly and yearly views. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to quickly find delivery person records. An Export option is also available; you can export delivery person order data, and the exported file includes all filtered records based on selected date range.",
      "Delivery Person Order Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Delivery Person Name: Name of the delivery executive",
          "Date & Time: Date and time of the order",
          "Order Number: Unique order ID",
          "Number of Packets: Total packets in the order",
          "Dispatch Status: Current status of the dispatch order",
      ] },
    ],
  },
  {
    title: "Merchant Order Summary Report",
    body: [
      "Merchant Order Summary Report helps track and analyze the complete lifecycle of merchant orders. It provides a summarized view of order distribution, delivery progress, payments, cancellations, and returns. This report is useful for performance analysis, business monitoring, and operational decision-making.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between monthly and yearly summaries. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to find specific merchant records. An Export option is also available; you can export merchant order summary data, and the exported file includes all filtered records based on selected date range.",
      "Merchant Order Summary Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Merchant: Name of the merchant",
          "Date: Report date",
          "Pending: Number and amount of pending orders",
          "Assigned: Number of orders assigned",
          "Picked Up: Number of orders picked up",
          "Dispatched: Number of orders dispatched",
          "Delivered: Number of orders successfully delivered",
          "Paid: Number and amount of paid orders",
          "Canceled: Number of canceled orders",
          "Returned: Number and amount of returned orders",
      ] },
      "Each status helps in understanding the order flow and financial performance of the merchant.",
    ],
  },
  {
    title: "Customer Order Summary Report",
    body: [
      "Customer Order Summary Report helps track and analyze the complete order lifecycle for customers. It provides a summarized view of order status distribution along with count and amount details for each stage. This report is useful for monitoring customer order behavior, payment status, delivery progress, and overall operational performance.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between daily, monthly, and yearly summaries. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to quickly find specific customer records. An Export option is also available; you can export customer order summary data, and the exported file includes all filtered records based on selected date range.",
      "Customer Order Summary Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Customer Name: Name of the customer",
          "Date: Report date",
          "Pending: Number and amount of pending orders",
          "Assigned: Number of orders assigned",
          "Picked Up: Number of orders picked up",
          "Dispatched: Number of orders dispatched",
          "Delivered: Number of orders successfully delivered",
          "Paid: Number and amount of paid orders",
          "Canceled: Number of canceled orders",
          "Returned: Number and amount of returned orders",
      ] },
    ],
  },
  {
    title: "Load / Unload Report",
    body: [
      "Load / Unload Report helps track the complete operational flow of goods from loading at the merchant side to unloading at the destination. It provides visibility into time taken for each process, number of articles handled, and total value of shipments. This report is useful for logistics tracking, operational efficiency analysis, and delivery performance monitoring.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between daily, monthly, and yearly views. The report updates automatically based on the selected date.",
      "View Options: The report includes two view categories — Merchant List (displays load/unload data based on merchants) and Vehicle List (displays load/unload data based on vehicles). These views help analyze operations from both merchant and vehicle perspectives.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to quickly find specific records. An Export option is also available; you can export load/unload report data.",
      "Load / Unload Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Order Number: Unique order identification number",
          "Order Date & Time: Date and time of the order",
          "Driver Name: Name of the assigned driver",
          "Vehicle Number: Vehicle used for transport",
          "Merchant Name: Merchant associated with the order",
          "Load Start Time: Time when loading started",
          "Load End Time: Time when loading completed",
          "Load Duration Time: Total time taken for loading",
          "Unload Start Time: Time when unloading started",
          "Unload End Time: Time when unloading completed",
          "UnLoad Duration Time: Total time taken for unloading",
          "No of Article: Number of items handled in the shipment",
          "Total Value: Total value of goods in the shipment",
      ] },
    ],
  },
  {
    title: "Dispatch Customer Order Report",
    body: [
      "Dispatch Customer Order Report helps track customer orders from dispatch records. It provides visibility into order processing, dispatch status, and goods quantity for each customer. This report is useful for order tracking, delivery monitoring, and customer dispatch management.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between month-wise and year-wise views. The report updates automatically based on the selected date.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to quickly find specific customer orders. An Export option is also available; you can export dispatch customer order data, and the exported file includes all filtered records based on selected date range.",
      "Dispatch Customer Order Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Dispatch Customer Name: Name of the customer",
          "Date & Time: Date and time of order dispatch",
          "Order Number: Unique order identification number",
          "Number of Goods: Quantity of goods dispatched",
          "Dispatch Status: Current status of the dispatch order",
      ] },
    ],
  },
  {
    title: "Delivery Person Activity Report",
    body: [
      "Delivery Person Activity Report helps track end-to-end activities performed by delivery personnel across trips and orders. It provides visibility into customer assignments, merchant linkage, delivery execution, and order status. This report is useful for monitoring delivery performance, driver productivity, and operational efficiency.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "View Options: The report includes multiple view categories — Trip List (displays activity based on trip-wise data) and Driver List (displays activity based on driver-wise data). These views help analyze delivery performance from different perspectives.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export delivery person activity data; the exported file includes all filtered records based on selected date range.",
      "Delivery Person Activity Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Trip ID: Unique trip identification number",
          "Customer Name: Name of the customer",
          "Merchant Name: Name of the merchant",
          "Driver Name: Name of the delivery driver",
          "Order Date: Date when the order was placed",
          "Start Date / Time: Trip start timestamp",
          "End Date / Time: Trip end timestamp",
          "Vehicle Number: Vehicle used for delivery",
          "Order Number: Unique order identification number",
          "Duration: Total time taken for delivery activity",
          "Order Status: Current status of the order",
      ] },
    ],
  },
  {
    title: "Delivery Dispatch Summary Report",
    body: [
      "Delivery Dispatch Summary Report helps track and analyze delivery dispatch operations performed through milk-run or assigned trips. It provides visibility into trip execution, driver involvement, vehicle usage, and trip completion status. This report is useful for monitoring dispatch efficiency, trip completion rates, and overall delivery performance.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "View Options: The report includes the Trip List view, which displays dispatch summary based on trip-wise data and helps analyze dispatch operations at the trip level.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export delivery dispatch summary data; the exported file includes all filtered records based on selected date range.",
      "Delivery Dispatch Summary Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Driver Name: Name of the assigned driver",
          "Milk Run Vehicle No.: Vehicle used for milk-run delivery",
          "Trip Date: Date of the trip",
          "Trip Start: Trip start time",
          "Trip End: Trip end time",
          "Trip Status: Current status of the trip (e.g., completed, ongoing, etc.)",
      ] },
    ],
  },
  {
    title: "Schedule vs Actual ETA Report",
    body: [
      "Schedule vs Actual ETA Report helps compare expected trip timelines with real execution timings. It highlights differences between scheduled and actual arrival times, enabling better planning and operational efficiency. This report is useful for identifying delays, improving route planning, and enhancing delivery accuracy.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between daily, monthly, and yearly views. The report updates automatically based on the selected date.",
      "View Options: The report includes multiple view categories — Vehicle List, Trip List, and Driver List — to analyze performance from different operational perspectives.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Search & Export: A Search option is available to find specific trip records. An Export option is also available; you can export ETA comparison data.",
      "Schedule vs Actual ETA Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Trip ID: Unique trip identification number",
          "Vehicle: Vehicle used for the trip",
          "Driver: Driver assigned to the trip",
          "Location: Trip location or route",
          "Trip Schedule ETA: Expected arrival time as per schedule",
          "Trip Actual ETA: Actual recorded arrival time",
          "ETA Difference: Difference between scheduled and actual ETA",
          "Start Date & Time (Scheduled): Planned trip start details",
          "End Date & Time (Scheduled): Planned trip end details",
          "Start Date & Time (Actual): Actual trip start details",
          "End Date & Time (Actual): Actual trip end details",
          "Start Time / End Time: Additional timing breakdown for analysis",
      ] },
    ],
  },
  {
    title: "Material Collection Report",
    body: [
      "Material Collection Report helps track materials collected from different suppliers during transportation or milk-run operations. It provides visibility into collection activities linked with specific trips and vehicles. This report is useful for monitoring supply chain efficiency, material pickup tracking, and operational coordination.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date, or switch between day-wise, month-wise, and year-wise views. Additionally, a quick Day view option is available for immediate filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export material collection data; the exported file includes all filtered records based on selected date range.",
      "Material Collection Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Order Number: Unique order reference number",
          "Material Date: Date when material was collected",
          "Trip ID: Unique trip identification number",
          "Trip Date: Date of the trip",
          "Milk Run Vehicle No.: Vehicle used for collection (milk run vehicle)",
          "Vehicle Attendant: Person responsible for accompanying the vehicle",
          "Supplier Name: Name of the supplier from whom material was collected",
      ] },
    ],
  },
  {
    title: "Trip Activity Report",
    body: [
      "Trip Activity Report helps monitor the full lifecycle of a trip from start to end. It provides visibility into trip routes, timing, distance covered, and operational status. This report is useful for analyzing route efficiency, trip performance, and vehicle movement history.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "View Options: The report includes the Trip List view, which displays trip-wise activity details and helps analyze individual trip performance and tracking details.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export trip activity data; the exported file includes all filtered records based on selected date range.",
      "Trip Activity Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Trip ID: Unique trip identification number",
          "Trip Start Date: Date when the trip started",
          "Start Time: Trip start time",
          "Start Location: Starting point of the trip",
          "End Time: Trip end time",
          "Trip End Date: Date when the trip ended",
          "End Location: Ending point of the trip",
          "Duration: Total time taken for the trip",
          "Distance: Distance covered during the trip",
          "Status: Current status of the trip (Completed / Ongoing / Stopped etc.)",
      ] },
    ],
  },
  {
    title: "Trip Assigned vs Completed Report",
    body: [
      "Trip Assigned vs Completed Report helps monitor trip execution progress by comparing planned assignments with actual completion status. It provides visibility into how many trips are completed, ongoing, or still scheduled. This report is useful for tracking fleet utilization, driver performance, and trip execution efficiency.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. The report updates automatically based on the selected date.",
      "View Options: The report includes multiple view categories — Vehicle List (displays trip status based on vehicles) and Driver List (displays trip status based on drivers). These views help analyze performance from both vehicle and driver perspectives.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export trip assignment vs completion data; the exported file includes all filtered records based on selected date range.",
      "Trip Status Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle: Vehicle used for the trip",
          "Driver: Assigned driver",
          "Assigned: Number of assigned trips",
          "Completed: Number of completed trips",
          "Ongoing: Number of trips currently in progress",
          "Upcoming: Number of scheduled future trips",
      ] },
    ],
  },
  {
    title: "Trip vs Pickup Count Report",
    body: [
      "Trip vs Pickup Count Report helps track how many pickups are completed for each trip. It provides a clear summary of trip efficiency based on pickup activity. This report is useful for analyzing trip productivity, pickup distribution, and operational workload per trip.",
      "Report Data: This section displays a simple listing of trip-wise pickup counts in a table format.",
      { list: [
          "Sr. No: Serial number of the record",
          "Trip Name: Name or identifier of the trip",
          "Pickup Count: Total number of pickups completed in that trip",
      ] },
      "Export Functionality: An Export option is available on the screen. You can export trip vs pickup count data; the exported file includes all listed records. Useful for reporting and operational analysis.",
    ],
  },
  {
    title: "Pickup Point Report",
    body: [
      "Pickup Point Report helps manage and track all pickup locations associated with trips. It provides structured information about each pickup point to ensure proper route planning and operational clarity. This report is useful for logistics planning, route optimization, and pickup point management.",
      "Report Data: This section displays pickup point details in a table format.",
      { list: [
          "Sr. No: Serial number of the record",
          "Pickup Point Trip: Name or identifier of the trip associated with the pickup point",
          "Pickup Point Name: Name of the pickup location",
          "Pickup Point Code: Unique code assigned to the pickup point",
          "Pickup Point Priority: Priority level of the pickup point in the route",
          "Distance From Customer: Distance between pickup point and customer location",
      ] },
      "Export Functionality: An Export option is available on the screen. You can export pickup point report data; the exported file includes all listed records. Useful for route analysis and operational reporting.",
    ],
  },
  {
    title: "Live Trip Location Report",
    body: [
      "Live Trip Location Report helps monitor trip movement across different bus stops or checkpoints. It compares scheduled arrival times with actual arrival times to identify delays and track trip progress. This report is useful for live monitoring, route performance analysis, and operational tracking of trips.",
      "Trip Information: At the top of the screen, selected trip details such as trip ID and route/location name are displayed. This helps identify the specific trip being tracked.",
      "Report Table: The report displays stop-wise trip tracking data in a structured format.",
      { list: [
          "Sr. No: Serial number of the record",
          "Bus Stop: Name of the stop or checkpoint",
          "Scheduled Time of Arrival: Planned arrival time at the bus stop",
          "Actual Time of Arrival: Real recorded arrival time at the bus stop",
      ] },
      "Export Functionality: An Export option is available on the screen. You can export live trip location data; the exported file includes all bus stop-wise tracking records. Useful for reporting and operational analysis.",
    ],
  },
  {
    title: "Unplanned Trip Autorouting",
    body: [
      "Unplanned Trip Autorouting module helps generate optimized routes for vehicles based on selected inputs. Once a vehicle is selected and the date is provided, the system automatically creates a scheduled trip route and displays it on the map. This feature is useful for handling unscheduled trips, dynamic routing, and real-time operational planning.",
      "Vehicle Selection: A Vehicle List dropdown is available on the screen. You can select a vehicle from the dropdown; the selected vehicle will be used for route generation.",
      "Date Selection: A date calendar picker is available. Clicking on the date field opens a calendar popup. You can select the required date from the calendar; the selected date is used to generate the trip schedule.",
      "Route Generation: After selecting the vehicle and date, click on the Submit button. The system generates a scheduled trip automatically, and the generated route is displayed based on system-defined logic.",
      "Map View: A live map view is displayed on the screen. The map shows the generated route for the unplanned trip and helps visualize movement, stops, and route flow.",
    ],
  },
  {
    title: "Driver First and Last Ignition Report",
    body: [
      "Driver First and Last Ignition Report helps track vehicle usage based on ignition events. It shows when a vehicle was first started and last stopped during a trip, along with the total duration and distance covered. This report is useful for monitoring driver activity, trip execution timing, and vehicle utilization.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export ignition report data; the exported file includes all filtered records based on selected date range.",
      "Driver First and Last Ignition Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle identifier",
          "Vehicle Reg No: Registration number of the vehicle",
          "Driver: Name of the driver",
          "Date: Date of the record",
          "First Ignition: Time when vehicle was first started",
          "Last Ignition: Time when vehicle was last stopped",
          "Duration: Total time between first and last ignition",
          "Distance: Total distance covered during that period",
      ] },
    ],
  },
  {
    title: "Driver Activity Report",
    body: [
      "Driver Activity Report helps track end-to-end driver movements for each trip. It provides visibility into when a trip started, where it started, where it ended, and the total duration and distance covered. This report is useful for analyzing driver performance, trip efficiency, and operational tracking.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export driver activity data; the exported file includes all filtered records based on selected date range.",
      "Driver Activity Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle used for the trip",
          "Driver: Name of the driver",
          "Date: Trip date",
          "Status: Current status of the trip (Completed / Ongoing / etc.)",
          "Start Time: Trip start time",
          "Start Location: Starting point of the trip",
          "End Time: Trip end time",
          "End Location: Ending point of the trip",
          "Duration: Total time taken for the trip",
          "Distance (KM): Total distance covered during the trip",
      ] },
    ],
  },
  {
    title: "Driver Activity Summary Report",
    body: [
      "Driver Activity Summary Report helps analyze driver performance in terms of speed, trip duration, and distance covered. It provides both average and maximum speed along with overall trip summary details. This report is useful for evaluating driving efficiency, monitoring performance trends, and identifying high-performing or risky driving behavior.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export driver activity summary data; the exported file includes all filtered records based on selected date range.",
      "Driver Activity Summary Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle used for the trip",
          "Vehicle Reg No: Registration number of the vehicle",
          "Driver: Name of the driver",
          "Date: Trip date",
          "Start Time: Trip start time",
          "End Time: Trip end time",
          "Average Speed (Unit/HR): Average speed during the trip",
          "Max Speed (Unit/HR): Highest speed recorded",
          "Duration: Total time taken for the trip",
          "Distance: Total distance covered",
      ] },
    ],
  },
  {
    title: "Driver Attendance Report",
    body: [
      "Driver Attendance Report helps track driver presence and attendance records based on system logs. It shows when and where a driver was marked present along with their current status. This report is useful for monitoring driver availability, attendance tracking, and operational workforce management.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export driver attendance data; the exported file includes all filtered records based on selected date range.",
      "Driver Attendance Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Driver: Name of the driver",
          "Vehicle Number: Vehicle assigned or used",
          "Date-Time: Attendance timestamp",
          "Status: Attendance status (Present/Absent/Active/Inactive etc.)",
          "Location: Location where attendance was recorded",
      ] },
    ],
  },
  {
    title: "Driver License Expiry Report",
    body: [
      "Driver License Expiry Report provides a centralized view of drivers and their license validity information. This report helps administrators monitor driver license expiration dates and identify licenses that are approaching expiry or require renewal.",
      "The report displays driver details along with their license expiry dates, enabling efficient management of driver compliance records. Maintaining valid driver licenses is essential for legal operation, driver eligibility verification, and regulatory compliance.",
      "Date Selection: The report includes a date filter that allows you to view records based on a selected time period. Available options include Day, Month, and Year.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period and review additional driver license expiry records.",
      "Export Functionality: The report includes an Export option that enables you to download the displayed driver license expiry data for record maintenance, compliance tracking, reporting, and administrative purposes.",
      "Report Fields:",
      { list: [
          "Sr No. – Displays a sequential number assigned to each record.",
          "Image – Displays the profile image of the driver.",
          "Name – Displays the full name of the driver.",
          "Email – Displays the registered email address of the driver.",
          "Contact Number – Displays the driver's registered contact number.",
          "License Expiry Date – Displays the date on which the driver's license validity expires.",
      ] },
    ],
  },
  {
    title: "Inspection Due Report",
    body: [
      "Inspection Due Report helps track vehicles that are due for inspection based on system-defined schedules. It provides visibility into vehicle identification details along with upcoming inspection dates. This report is useful for maintenance planning, compliance management, and ensuring vehicle safety standards.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export inspection due data; the exported file includes all filtered records based on selected date range.",
      "Inspection Due Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle identifier",
          "Vehicle Registration Number: Registration details of the vehicle",
          "Inspection Due Date: Scheduled or upcoming inspection date",
      ] },
    ],
  },
  {
    title: "Insurance Expiry Report",
    body: [
      "Insurance Expiry Report helps track insurance validity for all vehicles in the system. It provides visibility into vehicle details along with their insurance expiry dates, helping ensure timely renewals and compliance. This report is useful for fleet compliance management, risk reduction, and insurance renewal planning.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export insurance expiry data; the exported file includes all filtered records based on selected date range.",
      "Insurance Expiry Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle identifier",
          "Vehicle Registration Number: Registration details of the vehicle",
          "Insurance Expiry Date: Date when vehicle insurance will expire",
      ] },
    ],
  },
  {
    title: "Registration Expiry Report",
    body: [
      "Registration Expiry Report helps track vehicle registration validity across the fleet. It provides vehicle identification details along with registration expiry dates, enabling proactive renewal planning and compliance management. This report is useful for fleet administrators, compliance teams, and maintenance managers responsible for keeping vehicle documentation up to date.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific date and switch between day-wise, month-wise, and year-wise views. Additionally, a Day view option is available for quick filtering.",
      "Navigation Controls: Back Button returns to the previous screen. Next Button moves forward or views additional report pages.",
      "Export Functionality: An Export option is available on the screen. You can export registration expiry data; the exported file includes all filtered records based on the selected date range.",
      "Registration Expiry Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle Number: Vehicle identifier",
          "Vehicle Registration Number: Official registration number of the vehicle",
          "Registration Expiry Date: Date on which the vehicle registration expires",
      ] },
    ],
  },
  {
    title: "Tax Expiry Report",
    body: [
      "Tax Expiry Report is designed to help fleet managers and administrators track the tax validity status of all vehicles within the fleet. Vehicle tax is a mandatory regulatory requirement, and operating a vehicle with an expired tax certificate may lead to penalties, legal issues, or operational disruptions.",
      "This report provides a clear overview of each vehicle's tax expiry date, enabling you to monitor upcoming renewals, identify overdue tax payments, and ensure continuous compliance with transportation regulations.",
      "Date Selection: The report includes a date selection option with Day (view tax expiry information for a selected day), Month, and Year options.",
      "Navigation Controls: Back moves to the previous date period and helps you review historical tax expiry records. Next moves to the next date period and is useful for checking upcoming tax expirations and future renewal schedules.",
      "Export Functionality: The report supports an Export feature that allows you to download the displayed report data. Benefits include maintaining tax renewal records, sharing information with management or compliance teams, preparing audit documentation, monitoring upcoming tax renewal schedules, and creating backup records.",
      "Report Fields:",
      { list: [
          "Sr. No – A sequential number assigned to each record displayed in the report.",
          "Vehicle Number – Displays the vehicle number assigned within the fleet management system.",
          "Vehicle Registration Number – Displays the official registration number of the vehicle.",
          "Tax Expiry Date – Displays the date on which the vehicle's tax validity expires.",
      ] },
    ],
  },
  {
    title: "Maintenance Overdues Report",
    body: [
      "Maintenance Overdues Report provides a consolidated view of vehicles that have overdue maintenance-related compliance items. This report helps administrators monitor critical vehicle documents and inspections that require immediate attention. It displays important expiry and due dates, enabling you to quickly identify vehicles with pending maintenance obligations and compliance requirements.",
      "The report presents vehicle-wise information for insurance, registration, tax, and inspection due dates in a single view.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options.",
      "Navigation Controls: Back navigates to the previous reporting period. Next navigates to the next reporting period for reviewing additional records.",
      "Export Functionality: The report includes an Export option that enables you to download the displayed maintenance overdue data for record-keeping, reporting, auditing, and compliance management purposes.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number for each record in the report.",
          "Vehicle Number – Displays the vehicle identification number or fleet-assigned vehicle number.",
          "Insurance Expiry Date – Displays the date on which the vehicle insurance coverage expires.",
          "Registration Expiry Date – Displays the vehicle registration validity expiration date.",
          "Tax Expiry Date – Displays the date on which the vehicle tax validity expires.",
          "Inspection Due Date – Displays the scheduled inspection due date for the vehicle.",
          "Action – Provides available actions related to the maintenance overdue record (view details, review records, or follow-up activities, depending on system configuration and user permissions).",
      ] },
    ],
  },
  {
    title: "Maintenance Dues Soon Report",
    body: [
      "Maintenance Dues Soon Report provides a comprehensive view of vehicles with upcoming maintenance and compliance-related due dates. This report helps fleet administrators monitor vehicles whose insurance, registration, tax, or inspection deadlines are approaching, allowing timely planning and action before they become overdue.",
      "The report serves as an early warning system by displaying important vehicle compliance dates in a single consolidated view.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options.",
      "Navigation Controls: Back navigates to the previous reporting period. Next navigates to the next reporting period, allowing you to review upcoming maintenance due records across different dates.",
      "Export Functionality: The report provides an Export option that allows you to download the displayed maintenance due records for documentation, reporting, analysis, and compliance tracking purposes.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record.",
          "Vehicle Number – Displays the vehicle identification number or fleet-assigned vehicle number.",
          "Insurance Expiry Date – Displays the upcoming expiry date of the vehicle's insurance coverage.",
          "Registration Expiry Date – Displays the upcoming expiry date of the vehicle registration.",
          "Tax Expiry Date – Displays the date on which the vehicle tax validity is scheduled to expire.",
          "Inspection Due Date – Displays the next scheduled inspection date for the vehicle.",
          "Action – Provides available actions associated with the maintenance due record.",
      ] },
    ],
  },
  {
    title: "Vehicle Expense Report",
    body: [
      "Vehicle Expense Report provides a consolidated view of expenses incurred for vehicles during a selected period. This report helps you monitor and analyze various vehicle-related costs by categorizing expenses into different expense types such as accidents, accessories, spare parts, fines, and fuel.",
      "The report allows you to selectively display or hide specific expense categories, making it easier to focus on particular expense types and review vehicle expenditure efficiently.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period.",
      "Export Functionality: The report provides an Export option that enables you to download the displayed expense information for reporting, auditing, record maintenance, and further analysis.",
      "Expense Categories — each can be individually displayed or hidden using the corresponding Show/Hide option:",
      { list: [
          "Accident (Show/Hide) – Expenses associated with vehicle accidents, such as accident repairs, damage restoration, insurance claim-related expenses, vehicle recovery costs, and other accident-related expenditures.",
          "Accessory (Show/Hide) – Expenses related to vehicle accessories, such as additional equipment installations, vehicle accessories and fittings, safety accessories, and operational enhancement equipment.",
          "Spare Parts (Show/Hide) – Expenses for spare parts and component replacements, such as engine components, brake parts, filters, electrical components, and replacement parts required during maintenance.",
          "Fine (Show/Hide) – Expenses related to penalties and fines, such as traffic violations, regulatory penalties, toll-related penalties, and compliance-related fines.",
          "Fuel (Show/Hide) – Expenses associated with vehicle fuel consumption, such as fuel purchases, refueling costs, and fuel-related operational expenses.",
      ] },
    ],
  },
  {
    title: "Vehicle Fuel Expense Report",
    body: [
      "Vehicle Fuel Expense Report provides detailed information about fuel-related expenses incurred by vehicles during a selected reporting period. This report helps you track fuel purchases, monitor fuel consumption records, review associated costs, and maintain accurate fuel expenditure history for each vehicle.",
      "The report displays vehicle-wise fuel purchase details, including odometer readings, fuel quantity purchased, purchase dates, and fuel costs.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options.",
      "Navigation Controls: Back allows you to move to the previous reporting period. Next allows you to move to the next reporting period and review fuel expense records for additional dates.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed fuel expense records for reporting, auditing, record maintenance, and further analysis.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each fuel expense record.",
          "Vehicle Number – Displays the vehicle identification number or fleet-assigned vehicle number.",
          "Current Odometer – Displays the vehicle odometer reading recorded at the time of the fuel purchase.",
          "Quantity – Displays the quantity of fuel purchased for the vehicle.",
          "Purchase Date – Displays the date on which the fuel purchase was made.",
          "Amount – Displays the total cost associated with the fuel purchase.",
      ] },
    ],
  },
  {
    title: "System Usage Audit Report",
    body: [
      "System Usage Audit Report provides detailed information about report access activities performed within the system. This report records and displays report usage statistics, allowing administrators to monitor how frequently reports are accessed and reviewed by users.",
      "The report serves as an audit trail for report-related activities by tracking report access URLs, access times, and access counts.",
      "Date Selection: The report includes a date selection option that allows you to view audit records for a specific day. The selected date determines the report access activities displayed within the audit report.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period and review historical report access records. Next allows you to navigate to the next reporting period.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each audit record.",
          "Reports Access Log URL – Displays the URL or system path of the report that was accessed.",
          "Reports Access Log Time – Displays the date and time at which the report was accessed.",
          "Reports Access Log Count – Displays the total number of times a particular report or report URL has been accessed during the selected reporting period.",
      ] },
    ],
  },
  {
    title: "Data Usage Report",
    body: [
      "Data Usage Report provides detailed information about the amount of mobile or device data consumed by each vehicle's tracking hardware. This report helps monitor network usage at the device level by displaying data consumption associated with each vehicle and its installed tracking unit.",
      "It presents a structured view of vehicle communication details, including device identifiers and total data usage in megabytes (MB).",
      "Date Selection: The report includes a date filter with Day, Month, and Year options.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period and view data usage records for other dates.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed data usage records for analysis, reporting, and record-keeping purposes.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record in the report.",
          "Vehicle Number – Displays the vehicle identification number assigned within the fleet management system.",
          "Register Number – Displays the official vehicle registration number.",
          "IMEI – Displays the IMEI number of the tracking device installed in the vehicle.",
          "Hardware Type – Displays the type or model of the tracking device installed in the vehicle.",
          "Vehicle Device Mobile No. – Displays the mobile number (SIM number) associated with the vehicle's tracking device used for data transmission.",
          "Data Usage (MB) – Displays the total data consumed by the vehicle's tracking device during the selected period.",
      ] },
    ],
  },
  {
    title: "Last Login Details Report",
    body: [
      "Last Login Details Report provides information about the most recent login activity of users in the system. This report helps track user access history by displaying the last login date and time along with user identity, role, and platform used for access.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options. Additionally, filters such as User Role and User Name may be available to refine the login records.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period and view additional login records.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed login details for auditing, reporting, and user activity tracking.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record.",
          "Name – Displays the name of the user who accessed the system.",
          "User Role – Displays the role assigned to the user (e.g., admin, driver, manager, etc.).",
          "Platform – Displays the platform used for login, such as web application or mobile application.",
          "Last Login Date – Displays the most recent login date (and time, if applicable) for the user.",
      ] },
    ],
  },
  {
    title: "Mobile App Usage Detail Report",
    body: [
      "Mobile App Usage Detail Report provides information about users who are actively using the mobile application. This report helps in tracking mobile app engagement based on user roles and usernames.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options. Additionally, filters such as User Role and User Name may be available to refine the report data.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed mobile app usage details for reporting, auditing, and usage analysis.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record.",
          "Profile Image – Displays the profile image of the user.",
          "User Name – Displays the name of the user using the mobile application.",
          "User Role – Displays the role assigned to the user (such as admin, driver, manager, etc.).",
      ] },
    ],
  },
  {
    title: "User Report",
    body: [
      "User Report provides a consolidated view of all users registered in the system. It displays user profile details along with their role, status, and verification information. This report helps in managing and reviewing user accounts across different application types such as transport management, fleet management, drivers, and customers.",
      "Date Selection: The report includes a date filter option with Day, Month, and Year.",
      "Filters: App Type Filter allows selection of application type such as Transport Manager, Fleet Manager, Driver, and Customer. Status Filter allows filtering users by Active or Inactive. Email Verification Filter allows filtering users by Verified or Not Verified.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed user data for administration, auditing, and reporting purposes.",
      "Report Fields:",
      { list: [
          "SL NO – Displays a sequential number for each user record.",
          "Image – Displays the profile image of the user.",
          "Name – Displays the full name of the user.",
          "Email – Displays the registered email address of the user.",
          "Contact Number – Displays the registered mobile number of the user.",
          "Role – Displays the assigned role of the user (e.g., Transport Manager, Fleet Manager, Driver, Customer).",
          "Status – Displays the current status of the user account (Active or Inactive).",
      ] },
    ],
  },
  {
    title: "User App Version Report",
    body: [
      "User App Version Report provides detailed information about the application versions installed on user devices across different platforms. This report helps in tracking which app version is currently being used by users and whether they are running the latest available version of the application.",
      "Filters: Platform Filter allows selection of the device operating system (iOS or Android). App Type Filter allows selection of different application categories such as Driver App, Fleet Manager App, and Delivery Partner App.",
      "Export Functionality: The report includes an Export option that allows you to download the app version details for version control, upgrade planning, and system monitoring.",
      "Report Fields:",
      { list: [
          "SL NO – Displays a sequential number assigned to each record.",
          "Image – Displays the profile image of the user.",
          "Name – Displays the name of the user associated with the device.",
          "User App Type – Indicates the type of application installed on the user's device (e.g., Driver App, Fleet Manager App, Delivery Partner App).",
          "Device OS – Displays the operating system of the device (iOS or Android).",
          "Device Id – Displays the unique identifier of the device used by the user.",
          "Installed Version – Displays the version of the application currently installed on the user's device.",
          "Available Version – Displays the latest available version of the application.",
      ] },
    ],
  },
  {
    title: "Message Report",
    body: [
      "Message Report provides a detailed log of messages exchanged within the system between different users or roles. This report helps track communication history by displaying sender, receiver, subject, and timestamp details in a structured format.",
      "Filters: Select Message From — a dropdown to select the sender of the message; used to filter messages based on the originating user. Select Message To — a dropdown to select the receiver of the message; used to filter messages based on message destination.",
      "Actions: Search Button is used to fetch message records based on selected filters and displays results in the report table. Clear Button resets all selected filters and clears the search results.",
      "Report Fields:",
      { list: [
          "SL NO – Displays a sequential number for each message record.",
          "From – Shows the sender of the message.",
          "To – Shows the recipient of the message.",
          "Subject – Displays the subject or title of the message.",
          "Date & Time – Displays the exact date and time when the message was sent.",
      ] },
    ],
  },
  {
    title: "Devices Without Vehicle Details Report",
    body: [
      "Devices Without Vehicle Details Report provides information about tracking devices that are currently not mapped or assigned to any vehicle in the system. This report helps identify unused, unassigned, or standalone devices such as RFID units, GPS trackers, cameras, or OBD devices.",
      "It presents a structured view of device-level activity, including location data, IMEI details, and last recorded status.",
      "Device Type Filter: A dropdown filter to categorize devices — All Devices (displays all unassigned devices), RFID (RFID-based devices), GPS (GPS tracking devices), Camera (camera-enabled devices), and OBD (On-Board Diagnostics devices).",
      "Search Functionality: A search bar is provided to locate devices using IMEI or serial number.",
      "Date Filter: A date selector (Day / Month / Year) is available to view device logs for a selected period.",
      "Export Functionality: The report includes an Export option that allows you to download device data. The exported file includes all visible records and can be used for inventory tracking, device audit, operational monitoring, and hardware management.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number for each device record.",
          "User Name – Displays the associated user name linked to the device activity (if available).",
          "Vehicle Number – Displays the vehicle number if temporarily or previously associated, or remains blank for unassigned devices.",
          "IMEI No – Displays the unique IMEI number of the tracking device.",
          "Latitude – Displays the last recorded latitude position of the device.",
          "Longitude – Displays the last recorded longitude position of the device.",
          "Activity Status – Displays the current status of the device such as Active, Inactive, Idle, or Disconnected.",
          "Speed – Displays the last recorded speed of the device at the time of logging.",
          "Logged Date – Displays the date on which the device last recorded data.",
          "Logged Time – Displays the time at which the device last recorded data.",
      ] },
    ],
  },
  {
    title: "GPS Signal Strength Report",
    body: [
      "GPS Signal Strength Report provides a visual representation of GPS signal quality during a selected trip. This report helps you analyze how effectively the tracking device was able to capture and transmit location data throughout the trip journey.",
      "It displays signal strength variations along the trip route on a map, categorized into different levels of GPS accuracy and connectivity quality.",
      "Trip Selection: A dropdown is provided to select the required trip. After selecting a trip, clicking on Search loads the GPS signal data. The report updates based on the selected trip route and duration.",
      "Search Functionality: The Search Button is used to fetch GPS signal data for the selected trip. Once clicked, the system processes trip tracking data and displays it on the map. You must select a trip before performing search.",
      "Map View: After selecting a trip and clicking search, a map is displayed showing GPS signal strength across the trip route, helping you understand how GPS performance varied during the journey.",
      "GPS Signal Strength Levels:",
      { list: [
          "Very Poor – Indicates extremely weak GPS signal quality. Location tracking may be inaccurate or delayed, with possible signal loss or missing data points.",
          "Poor – Indicates weak GPS signal strength. Some location updates may be delayed and route accuracy may be partially affected.",
          "Average – Indicates moderate GPS signal quality. Tracking is mostly stable with occasional fluctuations and minor deviations in location accuracy.",
          "Good – Indicates strong and stable GPS signal, with accurate and continuous location tracking and reliable route mapping with minimal deviation.",
      ] },
    ],
  },
  {
    title: "GSM Signal Strength Report",
    body: [
      "GSM Signal Strength Report provides a visual representation of the GSM network signal quality experienced by a selected vehicle during the chosen date. This report helps you analyze communication coverage and identify areas where the tracking device experienced strong or weak cellular network connectivity.",
      "The report categorizes signal strength into different levels and displays the information on a map.",
      "Date Selection: Select the desired date from the date picker. By default, the report can be viewed for the current day. The selected date determines the GSM signal data displayed on the map.",
      "Vehicle Selection: The report includes a vehicle selection dropdown containing all accessible vehicles. You can select a specific vehicle, view its GSM signal strength information, and analyze network coverage for that vehicle on the chosen date.",
      "Signal Strength Categories:",
      { list: [
          "Good – Locations where the device received strong GSM network coverage, generally indicating stable communication between the tracking device and the server.",
          "Average – Locations where the GSM signal was moderate; communication remains available, although signal quality may fluctuate.",
          "Poor – Locations where the GSM signal was weak; tracking data transmission may experience delays or reduced communication reliability.",
          "Very Poor – Locations where GSM network coverage was extremely weak or nearly unavailable; these areas may experience communication interruptions, delayed updates, or limited connectivity.",
      ] },
      "Map View: The report displays a map showing the GSM signal strength information for the selected vehicle and date, providing geographic visualization of network signal quality, signal strength distribution across the route, and easy identification of areas with strong and weak network connectivity.",
    ],
  },
  {
    title: "Tracking Device and SIM Card Details Report",
    body: [
      "Tracking Device and SIM Card Details Report provides complete information about tracking hardware installed in vehicles along with associated SIM card and device identification details. This report helps maintain visibility over vehicle tracking devices, their hardware configuration, and communication SIM assignments.",
      "It presents a structured record of each vehicle's tracking setup, including IMEI, SIM card number, serial number, and hardware type.",
      "Date Selection: The report includes a date filter with Day, Month, and Year options. Additionally, filters such as Vehicle List and Vehicle Type may be available to refine the report data.",
      "Navigation Controls: Back allows you to navigate to the previous reporting period. Next allows you to navigate to the next reporting period and view additional tracking device records.",
      "Export Functionality: The report includes an Export option that allows you to download the displayed tracking device and SIM card details for inventory tracking, audits, and system documentation.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record.",
          "Vehicle Number – Displays the vehicle identification number assigned within the fleet system.",
          "Vehicle Reg No – Displays the official registration number of the vehicle.",
          "Vehicle IMEI – Displays the IMEI number of the tracking device installed in the vehicle.",
          "Vehicle Type – Displays the category or type of the vehicle.",
          "SIM Card Number – Displays the SIM card number installed in the tracking device.",
          "Serial No – Displays the serial number of the tracking hardware device.",
          "Vehicle Hardware – Displays the type or model of the tracking hardware installed in the vehicle.",
      ] },
    ],
  },
  {
    title: "Alerts Report",
    body: [
      "Alerts Report provides a detailed log of vehicle alerts generated by the system based on driving behavior, safety events, and operational conditions. This report helps you monitor different types of alerts such as overspeeding, harsh braking, and harsh acceleration, along with their severity levels and location details.",
      "It presents a structured view of alert events with timestamped records and geolocation data.",
      "Date Selection: The report includes a date range filter — From Date (start date) and To Date (end date) — to view alerts generated within a specific time period.",
      "Filters: Vehicle Selection — a checkbox and dropdown to select one or more vehicles or view all vehicles. Alert Type Filter — a dropdown to filter alerts by type such as Overspeed, Hard Brake, Harsh Acceleration, and other system-defined alert types. Severity Filter — a dropdown to filter alerts by severity level: High, Critical, Medium, Low.",
      "Export Functionality: The report includes an Export option that allows you to download alert data. The exported file contains all records based on selected filters, including vehicle, alert type, and severity.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number for each alert record.",
          "Vehicle Number – Displays the vehicle associated with the alert event.",
          "Device IMEI – Displays the IMEI number of the tracking device that generated the alert.",
          "Alert Type – Displays the type of alert generated (e.g., overspeed, harsh braking, etc.).",
          "Alert Status – Displays the status of the alert (e.g., triggered, resolved, pending depending on system configuration).",
          "Alert Value – Displays the measured value that triggered the alert (e.g., speed value, acceleration level, etc.).",
          "Alert Date – Displays the date when the alert was generated.",
          "Alert Time – Displays the exact time when the alert occurred.",
          "Latitude – Displays the geographic latitude where the alert was triggered.",
          "Longitude – Displays the geographic longitude where the alert was triggered.",
      ] },
    ],
  },
  {
    title: "Monthly Device Uptime Report",
    body: [
      "Monthly Device Uptime Report provides a summarized view of tracking device availability and performance for each vehicle over a selected month. This report helps you monitor how consistently vehicle tracking devices remained active and connected to the system during the reporting period.",
      "It presents device-wise uptime details including tracking duration and uptime percentage.",
      "Date Selection: The report includes a date filter for selecting the required month or period. You can select a specific month, and the report updates based on the selected time period. Additionally, a Vehicle List filter is available to view uptime details for specific vehicles or the entire fleet.",
      "Export Functionality: The report includes an Export option that allows you to download monthly uptime data for analysis, reporting, and system monitoring.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each record.",
          "Vehicle Number – Displays the unique vehicle identification number associated with the device.",
          "Device IMEI – Displays the IMEI number of the tracking device installed in the vehicle.",
          "Device Type – Displays the type of tracking device installed (e.g., GPS, RFID, OBD, Camera, etc.).",
          "Track Duration – Displays the total duration for which the device actively transmitted tracking data during the selected month.",
          "Uptime (%) – Displays the percentage of time the device remained active and successfully communicated with the system.",
      ] },
    ],
  },
  {
    title: "List of Tracking Devices",
    body: [
      "List of Tracking Devices Report provides a complete inventory view of all tracking devices and hardware types configured within the system. This report helps you identify and categorize devices installed across vehicles based on their hardware manufacturer or device type.",
      "It presents a structured view of all tracking devices associated with vehicles, including IMEI details, hardware classification, and vehicle mapping information. Additionally, the report includes a summary breakdown of tracking hardware types, showing the count of devices available under each hardware category.",
      "Device Type Summary: The report displays a categorized summary of tracking devices based on hardware type. The hardware categories include:",
      { list: [
          "National Meters Hardware",
          "Streamax",
          "Wialon IPS",
          "KPIT Hardware",
          "Delivery App Devices",
          "Autocop Hardware",
          "Teltonika FM",
          "Dahua MDVR",
          "Garmin",
          "LGPS Hardware",
          "KPIT MDVR",
          "Driver Console App",
          "Ruptela",
      ] },
      "Each category shows the total number of devices associated with it in the system.",
      "Vehicle List Filter: A Vehicle List dropdown is available to filter tracking devices based on vehicle selection. You can view devices assigned to a specific vehicle or all available tracking devices across the fleet.",
      "Report Fields:",
      { list: [
          "Sr. No – Displays a sequential number assigned to each tracking device record.",
          "Vehicle Number – Displays the vehicle identification number associated with the tracking device.",
          "Registration No – Displays the official registration number of the vehicle linked to the device.",
          "IMEI No – Displays the IMEI number of the tracking device installed in the vehicle.",
          "Vehicle Type – Displays the category or type of vehicle associated with the device.",
          "Hardware Name – Displays the name of the tracking hardware or device manufacturer installed in the vehicle.",
      ] },
      "Export Functionality: The report includes an Export option that allows you to download tracking device details for inventory tracking, device management, auditing, and reporting purposes.",
    ],
  },
  {
    title: "Vehicle Parking Attendance Report",
    body: [
      "The Vehicles By Parking Station tab provides a visual and tabular overview of vehicles parked at different parking stations.",
      "Map View: By default, the report displays a map showing parking station locations and associated vehicle information. The map helps you view parking station locations geographically, identify vehicle distribution across parking stations, monitor parking utilization visually, and quickly locate parking facilities. You can click the Hide Map option to collapse the map and focus on the tabular report view.",
      "Filters: Use the Station Name dropdown to select a specific parking station. Use the Vehicle List dropdown to select a specific vehicle. Use the Calendar icon to choose a specific date. The Filter By dropdown allows you to filter parking records based on parking status — Still Parked (vehicles currently parked at the selected station) and Checkout (vehicles that have already exited the parking station).",
      "Parking Station Details: Below the map, the system displays a list of parking stations with Sr. No., Station Name, Vehicle Count, Parking Capacity, and Action (options to view additional parking station details and associated vehicle information).",
      "Vehicles Check-In / Check-Out Report: Provides detailed information about vehicle entry and exit activities at parking stations. Filters include Vehicle List, Date Selection, and Filter By. Fields include Sr. No., Vehicle Number, Station Name, Check-In Time, Check-In Date, Check-Out Time, and Check-Out Date.",
      "Vehicles Parking Data Report: Provides detailed parking history records for individual vehicles, including parking slots, parking duration, locations, and parking status. Fields include Sr. No., Vehicle Number, Slot Name, Check-In Time, Check-In Date, Check-Out Date, Check-Out Time, Address, and Status (Parked, Checked Out, Occupied, Available).",
      "Export Functionality: You can export parking station data and check-in/check-out records for capacity planning, utilization analysis, management reporting, attendance verification, vehicle movement audits, and historical record keeping.",
    ],
  },
  {
    title: "Parking Check-In and Check-Out Report",
    body: [
      "To view the parking check-in and check-out details of vehicles, navigate to the Parking Check-In / Check-Out Report section. This report provides a detailed log of vehicle movement in and out of parking stations along with timestamps, duration, and location details.",
      "Report Overview: The report allows you to track and monitor vehicle parking activities for a selected date range. It helps you analyze how long each vehicle stayed at a parking station and where the activity took place.",
      "Date Selection: At the top of the screen, a date filter dropdown (Day / Month / Year) is available. You can select a specific day to view daily parking activity, a month to view monthly records, or a year for a yearly parking summary. Once selected, the report data updates automatically.",
      "Navigation: Back Button returns to the previous screen. Next Button navigates forward or views additional report pages.",
      "Vehicle Report Table:",
      { list: [
          "Sr. No: Serial number of the record",
          "Vehicle No: Registration number of the vehicle",
          "Parking Station: Name of the parking location where the vehicle was recorded",
          "Check In: Date and time when the vehicle entered the parking station",
          "Check Out: Date and time when the vehicle exited the parking station",
          "Duration: Total time the vehicle remained in the parking station",
          "Address: Location details of the parking station or activity",
      ] },
    ],
  },
  {
    title: "Vehicle Assignment History Report",
    body: [
      "Vehicle Assignment History report helps you track past and current assignments of vehicles with drivers and helpers. It provides visibility into assignment duration and helps in maintaining accountability and operational tracking.",
      "This report is useful for analyzing workforce allocation and vehicle usage history.",
      "Date Range Selection: At the top of the screen, a date range filter is available. You can select a Start Date and End Date, and the report displays assignment records within the selected period. Data updates automatically based on the selected range.",
      "Tabs / View Options: The module contains two views — Vehicle List (shows assignment history based on vehicles) and Driver List (shows assignment history based on drivers).",
      "Assignment History Table:",
      { list: [
          "Sr No: Serial number of the record",
          "Vehicle: Assigned vehicle name or number",
          "Driver: Driver assigned to the vehicle",
          "Helper: Helper assigned along with the driver",
          "Start Date: Date when the assignment started",
          "End Date: Date when the assignment ended",
      ] },
    ],
  },
  {
    title: "Trip Manifest Report",
    body: [
      "The Trip Manifest Report provides a consolidated manifest of trips, bringing together the key details of each trip in a single view for record-keeping and operational reference.",
      "Date Selection: Use the date filter at the top of the screen to select the period for which you want to view trip manifest records.",
      "Navigation Controls: Use the Back and Next buttons to move between reporting periods.",
      "Export Functionality: An Export option is available to download the trip manifest data for reporting and documentation purposes.",
      "The report displays trip-wise manifest details such as trip identification, assigned vehicle, driver, route or stop information, and schedule details.",
    ],
  },
];

export const reportRoutes = [
  "/ReportView",
  "/DispatchReport",
  "/RealTimeReport",
  "/ReportOverSpeedView",
  "/VehicleLocationSignal",
  "/VehicleLocationReport",
  "/VehiclesBasedStatus",
  "/VehicleAssignmentHistory",
  "/VehicleCountAndCapacity",
  "/VehicleRunningReport",
  "/VehicleparkingSlot",
  "/VehicleFullData",
  "/ImmobiliserReport",
  "/Temperaturereport",
  "/DriverBehaviourReport",
  "/DispatchCustomerOrderReport",
  "/deliverypersonactivity",
  "/dispatchreports/deliverdispatchsummaryreport",
  "/TripScheduleEta",
  "/MaterialCollection",
  "/TripActivityReport",
  "/TripAssignedVersusCompleted",
  "/TripVersusPickupCount",
  "/PickUpPointReport",
  "/LiveTripLocationReport",
  "/TripManifestReport",
  "/UnplannedTripsAutoroutingNew",
  "/DriverListignitionReport",
  "/DriverActivertiyReport",
  "/DriverActivetySummaryReport",
  "/DriverAttendanceReport",
  "/DriverLicenceExpiryReport",
  "/InspectionDueReport",
  "/InsuranceExpiryReport",
  "/RegistrationExpiryReport",
  "/TaxExpiryReport",
  "/MaintenanceOverduesReport",
  "/MaintenanceDuesSoonReport",
  "/VehicleExpenseReport",
  "/VehicleFuelExpenseReport",
  "/VehicleFuelLevelReport",
  "/ReportsAndStatisticsUsageAudit",
  "/DataUsageR",
  "/lastLoginDetailsR",
  "/MobileAppDetailsR",
  "/mobleAppUsageDetailsR",
  "/UsersReport",
  "/UserAppsVersionReport",
  "/MessageReport",
  "/DevicesWithoutVehicleDetails",
  "/GPSStrengthReport",
  "/GSMReport",
  "/TrackingdevicesAndSimDetailsR",
  "/AlertsReport",
  "/MonthlyDeviceUptimeReport",
  "/ListOfTrackingDevices",
  "/Reports/ParkingStationReport",
];

export default helpContent;
